import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";

type Language = "nl" | "en";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations = {
  nl: {
    // Navbar
    "nav.services": "Diensten",
    "nav.approach": "Werkwijze",
    "nav.about": "Over ons",
    "nav.contact": "Contact",
    "nav.skipToMain": "Spring naar hoofdinhoud",

    // About Page
    "about.title": "Over ons",
    "about.intro.p1":
      "Wij zijn een groeiend professioneel bureau dat zich inzet om organisaties te helpen bij het behalen en behouden van digitale toegankelijkheidsnormen. Onze aanpak is helder, gestructureerd en gebaseerd op samenwerking. We richten ons op het creëren van praktische inzichten die teams direct kunnen toepassen, zonder onnodige complexiteit of technisch jargon.",
    "about.intro.p2":
      "EqualAccess is ontstaan vanuit een helder principe: digitale toegankelijkheid moet begrijpelijk, haalbaar en duurzaam zijn voor elke organisatie. Veel bedrijven erkennen het belang van toegankelijkheid, maar worstelen met de complexiteit van WCAG 2.2. De richtlijnen zijn technisch, het proces kan onduidelijk zijn en het vinden van praktische oplossingen vraagt vaak om meer dan een checklist.",
    "about.intro.p3": "Daar biedt EqualAccess een uitkomst.",
    "about.intro.p4":
      "Elke audit die we uitvoeren levert meer op dan alleen nalevingsdata. We vertalen de bevindingen naar duidelijke acties en helpen organisaties om hun toegankelijkheid stap voor stap te verbeteren. Voor klanten die op de lange termijn toegankelijk willen blijven, bieden we jaarlijkse programma's met periodieke heraudits, updates en voortdurende ondersteuning. Websites evalueren en toegankelijkheid zou daarin moeten meegroeien. ",
    "about.intro.p5":
      "EqualAccess werkt samen met mkb-bedrijven, publieke instellingen, grotere organisaties en digitale bureaus. Samen met onze klanten hebben een gemeenschappelijk doel: digitale ervaringen creëren die open en bruikbaar zijn voor iedereen",
    "about.closing": "EqualAccess - Your accessibility, our guarantee!",

    // Hero
    "hero.title": "Jouw website, onze garantie!",
    "hero.subtitle": "We helpen jouw website toegankelijk maken niet één keer, maar blijvend.",
    "hero.description": "Ontdek hoe wij jouw digitale toegankelijkheid naar het volgende niveau tillen met bewezen expertise en duurzame oplossingen.",
    "hero.cta.primary": "Gratis adviesgesprek",
    "hero.cta.quickscan": "Vraag een quickscan aan",
    "hero.cta.secondary": "Lees verder",
    "hero.benefit.1": "Geen jargon, wel expertise",
    "hero.benefit.2": "Doorlopende monitoring",
    "hero.benefit.3": "Samen naar resultaat",

    // Problem
    "problem.title": "Samen Naar Toegankelijkheid",
    "problem.intro":
      "De meeste bedrijven willen het goed doen. Maar toegankelijkheid kan ingewikkeld voelen. Zeker als je niet precies weet waar te beginnen. Herkenbaar?",
    "problem.challenge.1.title": "WCAG-regels voelen als een technische muur",
    "problem.challenge.1.desc": "Wij vertalen ze naar gewone taal en concrete stappen begrijpelijk voor iedereen.",
    "problem.challenge.2.title": "Audits zijn vaak een waslijst van fouten",
    "problem.challenge.2.desc": "Wij werken samen met je, zonder te wijzen maar wél met helder advies.",
    "problem.challenge.3.title": "Deadlines komen dichterbij",
    "problem.challenge.3.desc":
      "Samen zorgen we dat je op tijd compliant bent met een plan dat werkt voor jouw situatie.",
    "problem.closing": "Samen maken we toegankelijkheid begrijpelijk, haalbaar én duurzaam.",

    // Solution
    "solution.subtitle": "Toegankelijkheid Zoals Het Hoort",
    "solution.title": "Waarom EqualAccess",
    "solution.intro":
      "Wij helpen je stap voor stap. Met begrip, duidelijkheid en expertise. Zodat toegankelijkheid geen last is, maar een mooie stap vooruit.",
    "solution.usp.1.title": "We maken toegankelijkheid begrijpelijk",
    "solution.usp.1.desc":
      "Geen technische muur, maar een helder pad. Wij vertalen WCAG naar concrete acties die écht werken.",
    "solution.usp.2.title": "We denken mee, niet tegen",
    "solution.usp.2.desc": "We werken samen met jouw team om verbeteringen te realiseren.",
    "solution.usp.3.title": "Continu compliant, zonder zorgen",
    "solution.usp.3.desc":
      "Een website verandert constant. Daarom bieden wij doorlopende monitoring zodat je niet elk jaar opnieuw hoeft te beginnen.",
    "solution.usp.4.title": "Onafhankelijk én menselijk",
    "solution.usp.4.desc":
      "We verkopen geen dure implementatiepakketten. Wij geven eerlijk advies, gericht op wat jouw bedrijf écht nodig heeft.",
    "solution.usp.5.title": "Expertise met betrokkenheid",
    "solution.usp.5.desc":
      "We begrijpen de technische kant, maar ook de maatschappelijke waarde: gelijke toegang voor iedereen. Omdat het hoort.",

    // Process
    "process.title": "Samen Naar Toegankelijkheid",
    "process.intro":
      "In vier heldere stappen brengen we jouw website naar WCAG 2.2-niveau begrijpelijk uitgelegd, menselijk begeleid.",
    "process.step.1.title": "Kennismaking",
    "process.step.1.desc":
      "We luisteren naar jouw situatie en ambities. Wat is je vraag? Waar wil je naartoe? We nemen de tijd.",
    "process.step.2.title": "Scan & Analyse",
    "process.step.2.desc":
      "We screenen jouw website op toegankelijkheid. Helder, overzichtelijk en zonder jargon. Je weet precies waar je staat.",
    "process.step.3.title": "Advies op Maat",
    "process.step.3.desc":
      "Je krijgt concrete verbeterstappen — geen waslijst, maar een werkbaar plan. Inclusief prioriteiten en termijnen.",
    "process.step.4.title": "Monitoring & Ondersteuning",
    "process.step.4.desc":
      "We blijven meekijken. Doorlopend. Zodat jouw website toegankelijk blijft niet één keer, maar altijd.",

    // CTA
    "cta.title": "Zullen we samen kijken?",
    "cta.subtitle": "Maak een afspraak voor een gratis adviesgesprek. We denken graag met je mee.",
    "cta.feature.1": "Begrijpelijk uitgelegd en",
    "cta.feature.2": "Eerlijk advies",
    "cta.feature.3": "Snelle respons,",
    "cta.button": "Plan een gesprek",

    // Footer
    "footer.tagline": "Samen naar toegankelijkheid. Helder, menselijk en deskundig.",
    "footer.links.title": "Snelle links",
    "footer.links.about": "Over ons",
    "footer.links.services": "Diensten",
    "footer.links.approach": "Werkwijze",
    "footer.links.booking": "Belafspraak maken",
    "footer.contact.title": "Contact",
    "footer.company.title": "Bedrijfsgegevens",
    "footer.company.name": "EqualAccess B.V.",
    "footer.company.kvk": "KvK-nummer: 98044907",
    "footer.company.btw": "BTW-nummer: NL868336853B01",
    "footer.legal.title": "Juridisch",
    "footer.legal.privacy": "Privacyverklaring",
    "footer.legal.terms": "Algemene voorwaarden",
    "footer.legal.accessibility": "Toegankelijkheidsverklaring",
    "footer.copyright": "© 2025 EqualAccess B.V. Alle rechten voorbehouden.",

    // Contact Page
    "contact.title": "Contactformulier",
    "contact.intro":
      "Heb je een vraag of wil je weten hoe toegankelijk jouw website nu is? We denken graag met je mee. Zonder gedoe, zonder vakjargon. Laat je bericht achter en we reageren meestal binnen één werkdag.",
    "contact.info.title": "Neem contact op",
    "contact.info.email": "E-mail",
    "contact.info.phone": "Telefoon",
    "contact.form.title": "Stuur een bericht",
    "contact.form.message": "Bericht",
    "contact.form.message.placeholder": "Vertel ons wat we voor je kunnen betekenen...",
    "contact.form.firstName": "Voornaam",
    "contact.form.lastName": "Achternaam",
    "contact.form.organization": "Organisatie",
    "contact.form.email": "E-mailadres",
    "contact.form.phone": "Telefoonnummer",
    "contact.form.updates": "Stuur mij updates over digitale toegankelijkheid.",
    "contact.form.submit": "Verstuur mijn bericht",
    "contact.form.required": "vereist",
    "contact.form.success": "Bedankt voor je bericht! We nemen zo snel mogelijk contact met je op.",
    "contact.form.error": "Er is iets misgegaan. Probeer het later opnieuw.",
    "contact.form.validation.messageRequired": "Bericht is verplicht",
    "contact.form.validation.firstNameRequired": "Voornaam is verplicht",
    "contact.form.validation.lastNameRequired": "Achternaam is verplicht",
    "contact.form.validation.emailInvalid": "Ongeldig e-mailadres",
    "contact.form.validation.phoneInvalid": "Ongeldig telefoonnummer",
    "contact.booking.title": "Plan een gesprek",
    "contact.booking.description": "Boek direct een belafspraak in onze agenda.",
    "contact.booking.formTitle": "Bel afspraak inplannen",
    "contact.booking.selectDate": "Kies een datum",
    "contact.booking.selectTime": "Kies een tijd",
    "contact.booking.pickDate": "Selecteer een datum",
    "contact.booking.pickTime": "Selecteer een tijdstip",
    "contact.booking.name": "Naam",
    "contact.booking.notes": "Opmerkingen",
    "contact.booking.notesPlaceholder": "Waar kunnen we je mee helpen?",
    "contact.booking.submit": "Afspraak aanvragen",
    "contact.booking.validation.nameRequired": "Naam is verplicht",
    "contact.booking.validation.phoneRequired": "Telefoonnummer is verplicht",
    "contact.booking.validation.dateTimeRequired": "Selecteer een datum en tijd",
    "contact.booking.error": "Er is iets misgegaan bij het plannen van de afspraak.",
    "contact.booking.success.title": "Afspraak aangevraagd!",
    "contact.booking.success.description":
      "Wij nemen zo snel mogelijk contact met jouw op om de afspraak te bevestigen.",
    "contact.booking.success.newBooking": "Nog een afspraak maken",
    "contact.booking.fullFlow": "Uitgebreide flow →",

    // Privacy
    "privacy.title": "Privacyverklaring",
    "privacy.intro.title": "Inleiding",
    "privacy.intro.text":
      "EqualAccess B.V. respecteert je privacy en behandelt je persoonsgegevens met zorg. Deze privacyverklaring legt uit welke gegevens wij verzamelen, waarom we dat doen en wat je rechten zijn.",
    "privacy.data.title": "Welke gegevens verzamelen wij?",
    "privacy.data.text": "Wij verzamelen de volgende persoonsgegevens wanneer je contact met ons opneemt:",
    "privacy.data.item1": "Naam en contactgegevens (e-mailadres, telefoonnummer)",
    "privacy.data.item2": "Bedrijfsnaam en organisatiegegevens",
    "privacy.data.item3": "Communicatie via ons contactformulier",
    "privacy.data.item4": "Technische gegevens zoals IP-adres en browsertype (alleen functionele cookies)",
    "privacy.purpose.title": "Waarvoor gebruiken wij je gegevens?",
    "privacy.purpose.text": "Wij gebruiken je persoonsgegevens uitsluitend voor:",
    "privacy.purpose.item1": "Het beantwoorden van je vragen en verzoeken",
    "privacy.purpose.item2": "Het leveren van onze diensten op het gebied van webtoegankelijkheid",
    "privacy.purpose.item3": "Het verbeteren van onze website en dienstverlening",
    "privacy.rights.title": "Je rechten",
    "privacy.rights.text": "Je hebt het recht om:",
    "privacy.rights.item1": "Inzage te vragen in je persoonsgegevens",
    "privacy.rights.item2": "Je gegevens te laten corrigeren of verwijderen",
    "privacy.rights.item3": "Bezwaar te maken tegen verwerking van je gegevens",
    "privacy.rights.item4": "Je toestemming in te trekken",
    "privacy.rights.item5": "Een klacht in te dienen bij de Autoriteit Persoonsgegevens",
    "privacy.security.title": "Beveiliging",
    "privacy.security.text":
      "Wij nemen passende technische en organisatorische maatregelen om je persoonsgegevens te beschermen tegen verlies, misbruik en ongeautoriseerde toegang.",
    "privacy.contact.title": "Contact",
    "privacy.contact.text":
      "Voor vragen over deze privacyverklaring of het gebruik van jouw gegevens kan je contact met ons opnemen:",

    // Terms
    "terms.title": "Algemene Voorwaarden",
    "terms.effectiveDate": "Ingangsdatum: 1 januari 2026",

    // Article 1 - Definitions
    "terms.definitions.title": "Artikel 1. Definities",
    "terms.definitions.b2b":
      "Deze voorwaarden zijn uitsluitend van toepassing op zakelijke gebruikers (B2B). Ze zijn niet van toepassing op consumenten. Door gebruik te maken van enige dienst van EqualAccess bevestigt de Opdrachtgever te handelen in de uitoefening van beroep of bedrijf.",
    "terms.definitions.terms":
      'Algemene Voorwaarden: deze algemene voorwaarden van EqualAccess B.V., KvK 98044907, hierna "EqualAccess".',
    "terms.definitions.day": "Dag: kalenderdag.",
    "terms.definitions.continuous":
      "Doorlopende Overeenkomst: elke overeenkomst die voorziet in terugkerende levering van diensten gedurende een bepaalde periode, inclusief monitoring- en onderhoudsovereenkomsten.",
    "terms.definitions.deliveryTerm":
      "Levertermijn: de door EqualAccess aangegeven termijn voor voltooiing van de Prestatie of enig deel daarvan.",
    "terms.definitions.contractor": 'Opdrachtnemer: EqualAccess B.V., hierna "EqualAccess" genoemd.',
    "terms.definitions.client":
      "Opdrachtgever: elke natuurlijke of rechtspersoon die onderhandelt met of een overeenkomst aangaat met EqualAccess.",
    "terms.definitions.agreement":
      "Overeenkomst: elke overeenkomst gesloten tussen de Opdrachtgever en EqualAccess met betrekking tot de Prestatie.",
    "terms.definitions.performance":
      "Prestatie: de diensten of producten geleverd door EqualAccess, inclusief toegankelijkheidsaudits, advies, monitoring, training en gerelateerde activiteiten.",
    "terms.definitions.documentation":
      "Documentatie: alle handleidingen, specificaties, beschrijvingen, rapporten, technische documenten en andere materialen verstrekt door EqualAccess.",

    // Article 2 - Applicability
    "terms.applicability.title": "Artikel 2. Toepasselijkheid",
    "terms.applicability.text1":
      "Deze Algemene Voorwaarden zijn van toepassing op alle activiteiten uitgevoerd door EqualAccess, inclusief maar niet beperkt tot offertes, overeenkomsten, adviesdiensten, communicatie, workshops, analyses, demonstraties, audits, monitoring en elke vorm van samenwerking of interactie met de Opdrachtgever, ongeacht of dergelijke activiteiten direct leiden tot een formele Overeenkomst.",
    "terms.applicability.text2":
      "Deze Algemene Voorwaarden zijn ook van toepassing op alle rechtshandelingen, voorbereidende werkzaamheden, precontractuele besprekingen, onderhandelingen of verkennende gesprekken uitgevoerd door EqualAccess.",
    "terms.applicability.text3":
      "Eventuele voorwaarden van de Opdrachtgever worden uitdrukkelijk afgewezen tenzij schriftelijk aanvaard door EqualAccess.",
    "terms.applicability.text4":
      "Afwijkingen van deze Algemene Voorwaarden zijn alleen geldig indien schriftelijk overeengekomen.",

    // Article 3 - Quotations
    "terms.quotes.title": "Artikel 3. Offertes en Aanbiedingen",
    "terms.quotes.text1": "Alle aanbiedingen door EqualAccess zijn vrijblijvend tenzij uitdrukkelijk anders vermeld.",
    "terms.quotes.text2":
      "Aanbiedingen blijven geldig gedurende de daarin vermelde periode; indien geen periode is vermeld, geldt een geldigheid van veertien dagen.",
    "terms.quotes.text3": "Kennelijke vergissingen, rekenfouten en typfouten binden EqualAccess niet.",
    "terms.quotes.text4": "Wijzigingen in aanbiedingen zijn alleen bindend indien schriftelijk bevestigd.",

    // Article 4 - Formation
    "terms.formation.title": "Artikel 4. Totstandkoming van de Overeenkomst",
    "terms.formation.text1":
      "Een Overeenkomst komt tot stand wanneer EqualAccess de opdracht schriftelijk of elektronisch bevestigt.",
    "terms.formation.text2":
      "Indien de Opdrachtgever de aanbieding wijzigt, komt de Overeenkomst pas tot stand nadat EqualAccess de gewijzigde voorwaarden bevestigt.",
    "terms.formation.text3":
      "Indien de uitvoering aanvangt zonder schriftelijke bevestiging, wordt de uitgebrachte factuur geacht de Overeenkomst volledig weer te geven.",

    // Article 5 - Continuous Agreements
    "terms.continuous.title": "Artikel 5. Doorlopende Overeenkomsten",
    "terms.continuous.text1": "Doorlopende Overeenkomsten gelden voor de overeengekomen duur.",
    "terms.continuous.text2": "Bij onduidelijkheid wordt de duur geacht twaalf maanden te zijn.",
    "terms.continuous.text3":
      "Doorlopende Overeenkomsten kunnen niet voortijdig worden beëindigd zonder betaling van de resterende contractuele bedragen.",
    "terms.continuous.text4":
      "Na afloop wordt de Overeenkomst stilzwijgend verlengd tenzij schriftelijk opgezegd met inachtneming van een opzegtermijn van ten minste twee maanden.",

    // Article 6 - Obligations EqualAccess
    "terms.obligations.title": "Artikel 6. Verplichtingen van EqualAccess",
    "terms.obligations.text1": "EqualAccess verricht haar diensten op basis van een inspanningsverbintenis.",
    "terms.obligations.text2":
      "Diensten worden uitgevoerd met de nodige zorgvuldigheid, vakmanschap en conform overeengekomen specificaties.",
    "terms.obligations.text3":
      "EqualAccess mag derden inschakelen bij de uitvoering van de Prestatie en mag daarmee gepaard gaande kosten doorberekenen.",
    "terms.obligations.text4":
      "Kleine of niet-essentiële aanpassingen mogen worden doorgevoerd mits deze de kernfunctionaliteit niet beïnvloeden.",
    "terms.obligations.text5":
      "EqualAccess implementeert passende technische en organisatorische beveiligingsmaatregelen conform Europese gegevensbeschermingsnormen.",

    // Article 7 - Obligations Client
    "terms.clientObligations.title": "Artikel 7. Verplichtingen van de Opdrachtgever",
    "terms.clientObligations.text1":
      "De Opdrachtgever verstrekt alle benodigde informatie voor de juiste uitvoering van de Prestatie tijdig en nauwkeurig.",
    "terms.clientObligations.text2":
      "De Opdrachtgever staat in voor de juistheid en volledigheid van de verstrekte informatie.",
    "terms.clientObligations.text3": "Materialen dienen te worden aangeleverd in een gangbaar en bruikbaar formaat.",
    "terms.clientObligations.text4":
      "De Opdrachtgever garandeert dat alle aangeleverde materialen vrij zijn van rechten van derden of dat daarvoor toestemming is verkregen.",
    "terms.clientObligations.text5":
      "De Opdrachtgever verleent toegang tot systemen, omgevingen of infrastructuur indien noodzakelijk voor de uitvoering.",

    // Article 8 - Performance and Acceptance
    "terms.acceptance.title": "Artikel 8. Uitvoering en Acceptatie",
    "terms.acceptance.text1":
      "EqualAccess mag schriftelijke goedkeuring van specificaties verlangen voorafgaand aan aanvang.",
    "terms.acceptance.text2":
      "Indien werkzaamheden in fasen worden uitgevoerd, kan aanvang van een volgende fase worden opgeschort totdat de vorige is goedgekeurd.",
    "terms.acceptance.text3":
      "Acceptatie geschiedt bij oplevering, bij verstrijken van een overeengekomen testperiode, of bij ingebruikname door de Opdrachtgever.",
    "terms.acceptance.text4":
      "Kleine gebreken die operationeel gebruik niet belemmeren, rechtvaardigen geen weigering van acceptatie.",

    // Article 9 - Prices
    "terms.prices.title": "Artikel 9. Prijzen",
    "terms.prices.text1":
      "Alle prijzen zijn exclusief BTW en andere toepasselijke belastingen, tenzij uitdrukkelijk anders vermeld.",
    "terms.prices.text2":
      "Prijzen kunnen na drie maanden worden aangepast wegens omstandigheden zoals inflatie, wetswijzigingen of kostenstijgingen.",
    "terms.prices.text3":
      "Aanpassingen wegens wijzigingen in de opdracht kwalificeren als meerwerk en worden apart gefactureerd.",

    // Article 10 - Delivery Terms
    "terms.delivery.title": "Artikel 10. Levertermijnen",
    "terms.delivery.text1": "Levertermijnen zijn indicatief tenzij uitdrukkelijk anders overeengekomen.",
    "terms.delivery.text2":
      "Overschrijding van een levertermijn geeft de Opdrachtgever geen recht op schadevergoeding of ontbinding.",
    "terms.delivery.text3":
      "Een Prestatie wordt geacht te zijn geleverd wanneer EqualAccess de Opdrachtgever hiervan in kennis stelt en binnen vijf dagen geen bezwaar wordt gemaakt.",

    // Article 11 - Changes and Additional Work
    "terms.additionalWork.title": "Artikel 11. Wijzigingen en Meerwerk",
    "terms.additionalWork.text1": "Elke wijziging in de opdracht resulteert in meer- of minderwerk.",
    "terms.additionalWork.text2": "Meerwerk wordt apart gefactureerd.",
    "terms.additionalWork.text3":
      "Indien omstandigheden extra inspanningen vereisen boven de oorspronkelijke verwachtingen, mag EqualAccess extra kosten in rekening brengen.",

    // Article 12 - Intellectual Property
    "terms.ip.title": "Artikel 12. Intellectueel Eigendom",
    "terms.ip.text1":
      "Alle intellectuele eigendomsrechten op ontwikkelde rapporten, documentatie en adviezen blijven eigendom van EqualAccess, tenzij uitdrukkelijk schriftelijk anders is overeengekomen.",
    "terms.ip.text2": "De Opdrachtgever ontvangt een niet-exclusief gebruiksrecht voor de overeengekomen doeleinden.",
    "terms.ip.text3":
      "De Opdrachtgever mag technische beveiligingen of beschermingsmechanismen niet verwijderen of omzeilen.",

    // Article 13 - Payment
    "terms.payment.title": "Artikel 13. Betalingsvoorwaarden",
    "terms.payment.text1":
      "Facturen dienen te worden betaald binnen veertien dagen na factuurdatum, tenzij een andere betalingstermijn is vermeld in de offerte.",
    "terms.payment.text2": "Bij te late betaling is wettelijke handelsrente verschuldigd.",
    "terms.payment.text3":
      "Alle incassokosten als gevolg van te late of niet-betaling komen voor rekening van de Opdrachtgever.",

    // Article 14 - Suspension and Retention
    "terms.suspension.title": "Artikel 14. Opschorting en Eigendomsvoorbehoud",
    "terms.suspension.text1":
      "Leveringen blijven eigendom van EqualAccess totdat volledige betaling heeft plaatsgevonden.",
    "terms.suspension.text2": "Bij betalingsachterstand mag EqualAccess haar diensten opschorten.",

    // Article 15 - Warranty
    "terms.warranty.title": "Artikel 15. Garantie",
    "terms.warranty.text1":
      "EqualAccess biedt een garantieperiode van dertig dagen (één maand) vanaf de datum van Acceptatie zoals omschreven in Artikel 8.",
    "terms.warranty.text2":
      "Gedurende deze garantieperiode herstelt EqualAccess gebreken die verhinderen dat de diensten functioneren conform de overeengekomen specificaties, mits dergelijke gebreken schriftelijk binnen de garantieperiode worden gemeld.",
    "terms.warranty.text3":
      "De garantie is niet van toepassing op: problemen veroorzaakt door systemen van derden, integraties, omgevingen of wijzigingen buiten de controle van EqualAccess; wijzigingen aangebracht door de Opdrachtgever of derden; gebreken voortvloeiend uit oneigenlijk gebruik of gebruik in strijd met de Documentatie.",

    // Article 16 - Liability
    "terms.liability.title": "Artikel 16. Aansprakelijkheid",
    "terms.liability.text1":
      "De aansprakelijkheid van EqualAccess is beperkt tot directe schade tot een maximum van de vergoedingen betaald voor drie maanden diensten, tenzij dwingend recht anders voorschrijft.",
    "terms.liability.text2":
      "EqualAccess is nimmer aansprakelijk voor indirecte of gevolgschade, inclusief maar niet beperkt tot omzetderving, gegevensverlies, reputatieschade of bedrijfsonderbreking.",
    "terms.liability.text3":
      "EqualAccess is niet aansprakelijk voor verlies, opschorting, afwaardering of intrekking van certificeringen aan de zijde van de Opdrachtgever, wanneer dergelijke gevolgen voortvloeien uit storingen, uitval of gedragingen van externe leveranciers, hostingproviders, infrastructuurpartners of andere externe afhankelijkheden buiten de directe controle van EqualAccess.",

    // Article 17 - Force Majeure
    "terms.forceMajeure.title": "Artikel 17. Overmacht",
    "terms.forceMajeure.text":
      "In geval van overmacht mag EqualAccess haar verplichtingen opschorten of beëindigen zonder aansprakelijkheid.",

    // Article 18 - Confidentiality
    "terms.confidentiality.title": "Artikel 18. Geheimhouding",
    "terms.confidentiality.text1":
      "Beide partijen houden strikte geheimhouding in acht met betrekking tot alle vertrouwelijke informatie die zij ontvangen. EqualAccess zal te allen tijde maximale inspanningen leveren om geheimhoudingsnormen te handhaven door het gebruik van geheimhoudingsovereenkomsten (NDA's) met alle betrokken partijen, inclusief externe leveranciers, onderaannemers en externe dienstverleners die betrokken zijn bij de uitvoering van de Overeenkomst.",
    "terms.confidentiality.text2":
      "EqualAccess zal nimmer vertrouwelijke informatie over Opdrachtgevers, hun interne processen, systemen, gegevens of projectspecificaties aan andere Opdrachtgevers of externe partijen openbaar maken, tenzij wettelijk verplicht.",
    "terms.confidentiality.text3":
      "Tenzij uitdrukkelijk schriftelijk anders is overeengekomen, is het EqualAccess toegestaan om naar de Opdrachtgever te verwijzen als klant voor marketingdoeleinden, inclusief het tonen van het logo van de Opdrachtgever op de EqualAccess-website of in marketingmaterialen. Op schriftelijk verzoek van de Opdrachtgever zal EqualAccess dergelijk logo of verwijzingen verwijderen.",
    "terms.confidentiality.text4":
      "Geheimhoudingsverplichtingen blijven van kracht gedurende de Overeenkomst en vijf jaar na beëindiging daarvan.",

    // Article 19 - Non-Solicitation
    "terms.nonSolicitation.title": "Artikel 19. Relatiebeding",
    "terms.nonSolicitation.text":
      "De Opdrachtgever mag geen personeel van EqualAccess in dienst nemen of inschakelen gedurende de Overeenkomst en vijf jaar daarna.",

    // Article 20 - Amendments
    "terms.changes.title": "Artikel 20. Wijzigingen",
    "terms.changes.text":
      "EqualAccess mag deze Algemene Voorwaarden wijzigen; wijzigingen zijn van toepassing tenzij de Opdrachtgever schriftelijk bezwaar maakt binnen dertig dagen.",

    // Article 21 - Complaints
    "terms.complaints.title": "Artikel 21. Klachten",
    "terms.complaints.text": "Klachten dienen schriftelijk te worden ingediend binnen veertien dagen.",

    // Article 22 - Governing Law
    "terms.law.title": "Artikel 22. Toepasselijk Recht en Geschillen",
    "terms.law.text1": "Nederlands recht is van toepassing op alle Overeenkomsten.",
    "terms.law.text2": "Geschillen worden uitsluitend voorgelegd aan de bevoegde rechter te Amsterdam, Nederland.",

    // Article 23 - Entire Agreement
    "terms.entireAgreement.title": "Artikel 23. Volledige Overeenkomst",
    "terms.entireAgreement.text":
      "Deze Voorwaarden, samen met eventuele schriftelijke overeenkomst of offerte, vormen de volledige overeenkomst tussen de Partijen en vervangen alle eerdere besprekingen, beloften of verklaringen.",
    "terms.entireAgreement.disclaimer":
      "In geval van enige discrepantie tussen verschillende taalversies van deze Algemene Voorwaarden, prevaleert de Nederlandse versie.",

    // Accessibility Statement
    "accessibility.title": "Toegankelijkheidsverklaring",
    "accessibility.commitment.title": "Onze inzet",
    "accessibility.commitment.text":
      "EqualAccess B.V. streeft ernaar om deze website toegankelijk te maken voor iedereen, ongeacht beperking of gebruikte technologie. Wij willen dat alle bezoekers onze website kunnen gebruiken en begrijpen.",
    "accessibility.standards.title": "Toegankelijkheidsstandaard",
    "accessibility.standards.text":
      "Deze website voldoet aan WCAG 2.2 niveau AA. Dit betekent dat de website onder andere:",
    "accessibility.standards.item1": "Volledig bedienbaar is met toetsenbord",
    "accessibility.standards.item2": "Goed werkt met schermlezers en andere hulptechnologie",
    "accessibility.standards.item3": "Voldoende kleurcontrast heeft voor leesbaarheid",
    "accessibility.standards.item4": "Duidelijke koppen en een logische structuur heeft",
    "accessibility.measures.title": "Genomen maatregelen",
    "accessibility.measures.text": "Wij hebben de volgende maatregelen genomen om de toegankelijkheid te waarborgen:",
    "accessibility.measures.item1": "Toegankelijkheid is vanaf het begin onderdeel van het ontwerp",
    "accessibility.measures.item2": "Regelmatige audits en tests met schermlezers",
    "accessibility.measures.item3": "Duidelijke focus-indicatoren voor toetsenbordnavigatie",
    "accessibility.measures.item4": "Alt-teksten bij alle afbeeldingen",
    "accessibility.measures.item5": "Semantische HTML en ARIA-labels waar nodig",
    "accessibility.limitations.title": "Bekende beperkingen",
    "accessibility.limitations.text":
      "Wij werken continu aan het verbeteren van de toegankelijkheid. Als je ondanks onze inspanningen toch problemen ondervindt, horen we dat graag.",
    "accessibility.feedback.title": "Feedback",
    "accessibility.feedback.text": "Ondervindt je toegankelijkheidsproblemen op deze website? Laat het ons weten:",
    "accessibility.feedback.response": "Wij streven ernaar binnen 5 werkdagen te reageren.",
    "accessibility.enforcement.title": "Handhaving",
    "accessibility.enforcement.text":
      "Deze toegankelijkheidsverklaring is opgesteld op 18 november 2024. Als je niet tevreden bent met onze reactie, kan je contact opnemen met het College voor de Rechten van de Mens.",

    // Approach Page
    "approach.title": "Onze werkwijze",
    "approach.intro":
      "Onze werkwijze is gebouwd op drie principes: duidelijkheid, samenwerking en continuïteit. We willen dat elke klant precies weet waar ze staan, wat er nodig is, en hoe we dat samen bereiken.",
    "approach.step1.title": "Quickscan (optioneel, maar populair)",
    "approach.step1.description":
      "We beginnen met een korte, laagdrempelige scan van jullie website. Hiermee brengen we de grootste aandachtspunten direct in beeld.",
    "approach.step1.benefit1": "Binnen 24–48 uur resultaat",
    "approach.step1.benefit2": "Ideaal voor eerste kennismaking",
    "approach.step1.benefit3": "Geeft meteen richting",
    "approach.step2.title": "Volledige WCAG 2.2-audit",
    "approach.step2.description":
      "Kiezen jullie voor een volledige audit? Dan onderzoeken we de website pagina voor pagina op alle relevante WCAG 2.2-succescriteria.",
    "approach.step2.areasTitle": "We kijken naar:",
    "approach.step2.area1": "Techniek",
    "approach.step2.area2": "Content",
    "approach.step2.area3": "Design en gebruikservaring",
    "approach.step2.area4": "Toetsenbordnavigatie",
    "approach.step2.area5": "Kleur en contrast",
    "approach.step2.area6": "Multimedia",
    "approach.step2.area7": "Formulieren",
    "approach.step2.area8": "Structuur en semantiek",
    "approach.step2.footer": "Alles wordt zorgvuldig getest en gedocumenteerd.",
    "approach.step3.title": "Heldere rapportage",
    "approach.step3.description": "Het eindresultaat is een duidelijk en praktisch rapport dat inzicht geeft in:",
    "approach.step3.include1": "Wat al goed gaat",
    "approach.step3.include2": "Waar verbeterpunten liggen",
    "approach.step3.include3": "Welke prioriteit elk punt heeft",
    "approach.step3.include4": "Hoe het opgelost kan worden",
    "approach.step3.footer": "Een rapport in begrijpelijke taal, zodat iedereen begrijpt wat er moet gebeuren.",
    "approach.step4.title": "Persoonlijke toelichting",
    "approach.step4.description":
      "We nemen het rapport persoonlijk met jullie door. Via videocall of op locatie bespreken we:",
    "approach.step4.topic1": "De bevindingen",
    "approach.step4.topic2": "De impact",
    "approach.step4.topic3": "De volgorde van oplossen",
    "approach.step4.topic4": "Jullie vragen",
    "approach.step4.footer": "Zo weet iedereen precies waar hij aan toe is.",
    "approach.step5.title": "Begeleiding bij implementatie",
    "approach.step5.description":
      "Wil de organisatie ondersteuning bij het doorvoeren van de verbeteringen? Geen probleem. We kunnen meekijken, meedenken en begeleiden bij:",
    "approach.step5.support1": "Development",
    "approach.step5.support2": "Contentaanpassingen",
    "approach.step5.support3": "Design",
    "approach.step5.support4": "Testing",
    "approach.step5.footer": "We werken samen met jullie team of met jullie webbouwer.",
    "approach.step6.title": "Monitoring & jaarabonnementen",
    "approach.step6.description":
      "Toegankelijkheid verandert continu. Daarom bieden we jaarabonnementen voor bedrijven die structureel toegankelijk willen blijven.",
    "approach.step6.includes": "In zo'n abonnement zitten bijvoorbeeld:",
    "approach.step6.feature1": "Periodieke heraudits",
    "approach.step6.feature2": "Updates volgens nieuwe richtlijnen",
    "approach.step6.feature3": "Monitoring van belangrijke pagina's",
    "approach.step6.feature4": "Ondersteuning bij vragen",
    "approach.step6.feature5": "Jaarlijkse toegankelijkheidsverklaring",
    "approach.step6.footer": "Zo blijft jouw website aantoonbaar compliant én up-to-date.",
    "approach.step7.title": "Certificering (indien gewenst)",
    "approach.step7.description":
      "Is de website volledig toegankelijk volgens WCAG 2.2? Dan kunnen we een toegankelijkheidsverklaring opstellen die voldoet aan de Europese vereisten.",
    "approach.step7.footer": "Perfect voor vertrouwen richting klanten, partners en overheden.",
    "approach.summary.title": "In het kort",
    "approach.summary.description":
      "Onze werkwijze is gebouwd op drie principes: duidelijkheid, samenwerking en continuïteit. We willen dat elke klant precies weet waar ze staan, wat er nodig is, en hoe we dat samen bereiken.",

    // Cookies
    "cookies.title": "Deze website gebruikt cookies",
    "cookies.description":
      "Wij gebruiken alleen functionele cookies die noodzakelijk zijn voor de werking van deze website. Deze cookies verzamelen geen persoonlijke gegevens en vereisen geen toestemming onder de AVG.",
    "cookies.accept": "Accepteren",
    "cookies.decline": "Weigeren",

    // Services Page
    "services.title": "Onze diensten",
    "services.intro":
      "EqualAccess biedt een compleet pakket aan diensten om jouw website toegankelijk te maken en te houden. Van snelle scans tot volledig begeleide trajecten.",
    "services.pricing.title": "Ook als het om prijzen gaat.",
    "services.pricing.description":
      "We werken met transparante, vaste tarieven zodat je precies weet waar je aan toe bent.",
    "services.pricing.disclaimer": "Alle bedragen zijn vanaf-prijzen en exclusief btw.",
    "services.quickscan.title": "Quickscan",
    "services.quickscan.description":
      "Een snelle, laagdrempelige scan van jouw website die de belangrijkste toegankelijkheidsproblemen in kaart brengt.",
    "services.quickscan.feature1": "Resultaat binnen 5 werkdagen",
    "services.quickscan.feature2": "Overzicht van de grootste knelpunten",
    "services.quickscan.feature3": "Ideaal voor een eerste kennismaking",
    "services.quickscan.feature4": "Heldere prioritering van aandachtspunten",
    "services.quickscan.price": "€395",
    "services.quickscan.cta": "Direct aanvragen",
    "services.mostPopular": "Meest gekozen",
    "services.audit.title": "Volledige WCAG 2.2 audit",
    "services.audit.description":
      "Een complete, professionele audit volgens alle WCAG 2.2 succescriteria. Pagina voor pagina getest en gedocumenteerd.",
    "services.audit.feature1": "Uitgebreid rapport met bevindingen",
    "services.audit.feature2": "Praktische verbeteradvies",
    "services.audit.feature3": "Test op alle WCAG 2.2 level AA criteria",
    "services.audit.feature4": "Inclusief persoonlijke toelichting",
    "services.audit.price": "Vanaf €1.995",
    "services.subscription.title": "Jaarabonnementen",
    "services.subscription.description":
      "Structurele monitoring en ondersteuning om jouw website continu toegankelijk te houden. Geen zorgen, wel compliance.",
    "services.subscription.feature1": "Periodieke heraudits",
    "services.subscription.feature2": "Updates bij nieuwe richtlijnen",
    "services.subscription.feature3": "Monitoring van belangrijke pagina's",
    "services.subscription.feature4": "Jaarlijkse toegankelijkheidsverklaring",
    "services.subscription.price": "Vanaf €999 per jaar",
    "services.implementation.title": "Implementatiebegeleiding",
    "services.implementation.description":
      "Hulp bij het doorvoeren van verbeteringen. We denken mee, kijken mee en begeleiden jouw team of webbouwer.",
    "services.implementation.feature1": "Begeleiding bij development",
    "services.implementation.feature2": "Hulp bij contentaanpassingen",
    "services.implementation.feature3": "Ondersteuning bij design wijzigingen",
    "services.implementation.feature4": "Testing en validatie",
    "services.implementation.price": "Op offerte",
    "services.certification.title": "Certificering & verklaringen",
    "services.certification.description":
      "Officiële toegankelijkheidsverklaring volgens Europese vereisten wanneer jouw website volledig compliant is.",
    "services.certification.feature1": "Volledige toegankelijkheidsverklaring",
    "services.certification.feature2": "Conform Europese wetgeving",
    "services.certification.feature3": "Vertrouwen naar klanten en partners",
    "services.certification.feature4": "Geschikt voor overheidsorganisaties",
    "services.certification.comingSoon": "Deze dienst is momenteel nog niet beschikbaar, maar werken we naartoe.",
    "services.certification.badge": "Binnenkort",
    "services.cta.title": "Benieuwd wat wij voor jou kunnen betekenen?",
    "services.cta.description":
      "Neem vrijblijvend contact met ons op en ontdek welke dienst het beste bij jouw situatie past.",
    "services.cta.button": "Plan een gesprek",

    // FAQ
    "faq.title": "Veelgestelde vragen",
    "faq.subtitle": "De meest gestelde vragen over digitale toegankelijkheid en WCAG 2.2",
    "faq.q1.question": "Wat is WCAG 2.2 en waarom is het belangrijk?",
    "faq.q1.answer":
      "WCAG 2.2 (Web Content Accessibility Guidelines) zijn internationale richtlijnen die ervoor zorgen dat websites voor iedereen toegankelijk zijn, inclusief mensen met een beperking. Vanaf 28 juni 2025 zijn Nederlandse organisaties wettelijk verplicht om hieraan te voldoen. Het gaat niet alleen om compliance, maar vooral om gelijke toegang tot digitale informatie voor álle gebruikers.",
    "faq.q2.question": "Hoe lang duurt een volledige WCAG-audit?",
    "faq.q2.answer":
      "Een volledige WCAG 2.2-audit duurt gemiddeld 2 tot 4 weken, afhankelijk van de grootte en complexiteit van jouw website. Een QuickScan daarentegen levert binnen 24-48 uur al een eerste overzicht van de belangrijkste aandachtspunten. Na de audit ontvang je een helder rapport met concrete verbeterpunten en prioriteiten.",
    "faq.q3.question": "Wat zijn de kosten van een toegankelijkheidsaudit?",
    "faq.q3.answer":
      "De kosten variëren op basis van de omvang en complexiteit van jouw website. Een QuickScan is een betaalbare, laagdrempelige optie om te beginnen. Voor een volledige WCAG 2.2-audit maken we graag een offerte op maat. Daarnaast bieden we jaarabonnementen aan voor structurele monitoring en ondersteuning. Neem vrijblijvend contact op voor een concreet voorstel.",
    "faq.q4.question": "Kan ik de verbeteringen zelf doorvoeren of helpen jullie daarbij?",
    "faq.q4.answer":
      "Beide opties zijn mogelijk. Ons rapport is zo helder opgesteld dat jouw eigen team of webbouwer er direct mee aan de slag kan. Maar als je begeleiding wenst, helpen we graag bij de implementatie. We kunnen meedenken, meekijken en concrete ondersteuning bieden bij development, design, content of testing. Je kiest zelf welke vorm van samenwerking het beste past.",

    // Knowledge Base / Kennisbank
    "nav.knowledge": "Kennisbank",
    "nav.knowledge.digital": "Digitale toegankelijkheid",
    "nav.knowledge.disabilities": "Soorten beperkingen",
    "nav.knowledge.wcag": "De WCAG",
    "nav.knowledge.wdo": "European Accessibility Act",

    // Knowledge CTA
    "knowledge.cta.title": "Hulp nodig bij toegankelijkheid?",
    "knowledge.cta.description": "Neem vrijblijvend contact met ons op en ontdek hoe wij jou kunnen helpen.",
    "knowledge.cta.button": "Plan een gesprek",

    // Digital Accessibility Page
    "knowledge.digital.title": "Digitale toegankelijkheid",
    "knowledge.digital.intro":
      "Digitale toegankelijkheid zorgt ervoor dat websites, apps en digitale documenten door iedereen te gebruiken zijn, ongeacht beperking of gebruikte technologie.",
    "knowledge.digital.what.title": "Wat is digitale toegankelijkheid?",
    "knowledge.digital.what.text":
      "Digitale toegankelijkheid betekent dat digitale producten en diensten zo ontworpen en gebouwd zijn dat ze voor iedereen bruikbaar zijn. Dit omvat mensen met een visuele, auditieve, motorische of cognitieve beperking, maar ook mensen die tijdelijk beperkt zijn of in bepaalde situaties.",
    "knowledge.digital.aspect1.title": "Techniek",
    "knowledge.digital.aspect1.desc":
      "De code van een website moet voldoen aan standaarden zodat hulptechnologie de content kan interpreteren.",
    "knowledge.digital.aspect2.title": "Gebruikerservaring",
    "knowledge.digital.aspect2.desc": "Een website moet intuïtief en eenvoudig te navigeren zijn voor alle gebruikers.",
    "knowledge.digital.aspect3.title": "Wet- en regelgeving",
    "knowledge.digital.aspect3.desc":
      "Steeds meer organisaties zijn wettelijk verplicht om hun digitale diensten toegankelijk te maken.",
    "knowledge.digital.aspect4.title": "Inclusiviteit",
    "knowledge.digital.aspect4.desc":
      "Toegankelijkheid gaat over gelijke kansen en het creëren van een inclusieve digitale wereld.",
    "knowledge.digital.why.title": "Waarom is het belangrijk?",
    "knowledge.digital.why.text":
      "Ongeveer 30% van de Nederlandse bevolking heeft een vorm van beperking (bron: CBS 2023), wereldwijd is dit circa 16% (bron: WHO). Digitale toegankelijkheid zorgt ervoor dat deze grote groep mensen volledig kan deelnemen aan de digitale samenleving. Daarnaast verbetert toegankelijkheid de gebruikservaring voor iedereen.",
    "knowledge.digital.who.title": "Voor wie is het relevant?",
    "knowledge.digital.who.text":
      "Digitale toegankelijkheid is relevant voor elke organisatie met een online aanwezigheid. Overheidsinstanties zijn wettelijk verplicht, maar ook bedrijven in de private sector krijgen steeds vaker te maken met toegankelijkheidseisen via de European Accessibility Act.",

    // Disabilities Page
    "knowledge.disabilities.title": "Soorten beperkingen",
    "knowledge.disabilities.intro":
      "Om digitale toegankelijkheid te begrijpen, is het belangrijk om te weten welke soorten beperkingen er zijn en hoe deze invloed hebben op het gebruik van websites.",
    "knowledge.disabilities.visual.title": "Visuele beperkingen",
    "knowledge.disabilities.visual.desc":
      "Dit omvat blindheid, slechtziendheid, kleurenblindheid en lichtgevoeligheid. Gebruikers zijn afhankelijk van schermlezers, brailleleesregels of aanpassingen in contrast en lettergrootte.",
    "knowledge.disabilities.auditory.title": "Auditieve beperkingen",
    "knowledge.disabilities.auditory.desc":
      "Doofheid en slechthorendheid maken audio-content ontoegankelijk. Ondertiteling, transcripties en visuele alternatieven zijn essentieel.",
    "knowledge.disabilities.motor.title": "Motorische beperkingen",
    "knowledge.disabilities.motor.desc":
      "Beperkte handfunctie, tremoren of verlamming maken muisgebruik lastig. Toetsenbordnavigatie en spraakbediening zijn belangrijke alternatieven.",
    "knowledge.disabilities.cognitive.title": "Cognitieve beperkingen",
    "knowledge.disabilities.cognitive.desc":
      "Dyslexie, ADHD, autisme en geheugenproblemen vragen om duidelijke taal, eenvoudige navigatie en consistente layouts.",
    "knowledge.disabilities.temporary.title": "Tijdelijke beperkingen",
    "knowledge.disabilities.temporary.text":
      "Beperkingen zijn niet altijd permanent. Een gebroken arm, oogoperatie of hersenschudding kan tijdelijk invloed hebben op hoe iemand een website gebruikt. Toegankelijke websites helpen ook deze gebruikers.",
    "knowledge.disabilities.situational.title": "Situationele beperkingen",
    "knowledge.disabilities.situational.text":
      "Soms is de omgeving de beperking: felle zon op een scherm, een lawaaierige omgeving of het vasthouden van een baby. Toegankelijkheidsmaatregelen helpen iedereen in deze situaties.",

    // WCAG Page
    "knowledge.wcag.title": "De WCAG",
    "knowledge.wcag.intro":
      "De Web Content Accessibility Guidelines (WCAG) zijn de internationale standaard voor digitale toegankelijkheid. Alles over de WCAG richtlijnen voor digitale toegankelijkheid vind je hier.",
    "knowledge.wcag.what.title": "Wat is de WCAG?",
    "knowledge.wcag.what.text":
      "WCAG staat voor Web Content Accessibility Guidelines. Het zijn internationaal geaccepteerde richtlijnen die beschrijven hoe webcontent toegankelijk gemaakt kan worden voor mensen met een beperking. De richtlijnen zijn ontwikkeld door het W3C (World Wide Web Consortium).",
    "knowledge.wcag.principles.title": "De vier principes",
    "knowledge.wcag.perceivable.title": "Waarneembaar",
    "knowledge.wcag.perceivable.desc":
      "Informatie en interface-elementen moeten op een manier gepresenteerd worden die gebruikers kunnen waarnemen.",
    "knowledge.wcag.operable.title": "Bedienbaar",
    "knowledge.wcag.operable.desc":
      "Interface-elementen en navigatie moeten bedienbaar zijn, ook met alleen een toetsenbord.",
    "knowledge.wcag.understandable.title": "Begrijpelijk",
    "knowledge.wcag.understandable.desc":
      "Informatie en de werking van de interface moeten begrijpelijk zijn voor gebruikers.",
    "knowledge.wcag.robust.title": "Robuust",
    "knowledge.wcag.robust.desc":
      "Content moet robuust genoeg zijn om betrouwbaar geïnterpreteerd te worden door verschillende hulptechnologieën.",
    "knowledge.wcag.levels.title": "Niveaus A, AA en AAA",
    "knowledge.wcag.levels.text":
      "WCAG heeft drie conformiteitsniveaus. Niveau A is het minimum, niveau AA is de standaard die vaak wettelijk vereist wordt, en niveau AAA is het hoogste niveau. De meeste organisaties richten zich op niveau AA.",
    "knowledge.wcag.versions.title": "WCAG versies",
    "knowledge.wcag.versions.text":
      "WCAG 2.0 verscheen in 2008, gevolgd door 2.1 in 2018 en 2.2 in 2023. Elke nieuwe versie voegt criteria toe maar behoudt de bestaande. WCAG 2.2 Level AA is momenteel de standaard in de Nederlandse en Europese wetgeving.",
    "knowledge.wcag.related.title": "Lees ook",
    "knowledge.wcag.link1": "Wat is digitale toegankelijkheid?",
    "knowledge.wcag.link2": "Soorten beperkingen",
    "knowledge.wcag.link3": "European Accessibility Act (EAA)",
    "knowledge.wcag.link4": "Toegankelijkheidstools",
    "knowledge.wcag.link5": "Toegankelijkheid checklist",

    // EAA Page (formerly WDO)
    "knowledge.wdo.title": "European Accessibility Act (EAA)",
    "knowledge.wdo.intro":
      "De European Accessibility Act (EAA) is sinds 28 juni 2025 van kracht. Deze Europese wet verplicht ook bedrijven in de private sector om hun digitale producten en diensten toegankelijk te maken.",
    "knowledge.wdo.about.title": "Wat is de EAA?",
    "knowledge.wdo.about.text":
      "De European Accessibility Act is een Europese richtlijn die toegankelijkheidseisen stelt aan producten en diensten. Anders dan eerdere wetgeving die alleen voor overheden gold, raakt de EAA ook het bedrijfsleven. De wet is op 28 juni 2025 in werking getreden en geldt nu voor alle EU-lidstaten.",
    "knowledge.wdo.who.title": "Voor wie geldt de EAA?",
    "knowledge.wdo.who.desc":
      "De EAA geldt voor bedrijven die producten en diensten aanbieden in sectoren zoals e-commerce, bankieren, transport, telecommunicatie en elektronische apparaten. Ook webshops en online dienstverleners vallen onder deze wet.",
    "knowledge.wdo.when.title": "Sinds wanneer?",
    "knowledge.wdo.when.desc":
      "De EAA is op 28 juni 2025 in werking getreden. Bedrijven moeten sindsdien voldoen aan de toegankelijkheidseisen. Producten en diensten die vóór deze datum op de markt waren, krijgen in sommige gevallen een overgangsperiode.",
    "knowledge.wdo.what.title": "Wat moet er gebeuren?",
    "knowledge.wdo.what.desc":
      "Websites, webshops en apps moeten voldoen aan WCAG 2.1 niveau AA. Dit betekent dat digitale diensten waarneembaar, bedienbaar, begrijpelijk en robuust moeten zijn voor alle gebruikers, inclusief mensen met een beperking.",
    "knowledge.wdo.consequences.title": "Consequenties",
    "knowledge.wdo.consequences.desc":
      "Bedrijven die niet voldoen aan de EAA kunnen te maken krijgen met boetes, claims van consumenten en reputatieschade. Toezichthouders kunnen handhavend optreden.",
    "knowledge.wdo.eaa.title": "En de Wet Digitale Overheid?",
    "knowledge.wdo.eaa.text":
      "De Wet Digitale Overheid (WDO) was de voorloper voor overheden. Deze Nederlandse wet verplichtte overheidsorganisaties al sinds 2019-2021 om toegankelijk te zijn. De EAA bouwt hierop voort maar breidt de verplichting uit naar de private sector. Voor overheden blijft de WDO gelden, voor bedrijven is nu de EAA leidend.",
    "knowledge.wdo.action.title": "Wat nu?",
    "knowledge.wdo.action.text":
      "De EAA is nu van kracht. Als jouw website of webshop nog niet toegankelijk is, is het zaak om snel actie te ondernemen. Begin met een audit om te weten waar je staat, en werk stap voor stap naar volledige compliance.",

    // Knowledge Base - Sources
    "knowledge.sources.title": "Bronnen",
    "knowledge.sources.government": "Overheidsportaal digitale toegankelijkheid",
    "knowledge.sources.accessibility": "Toegankelijke overheidswebsites",
    "knowledge.sources.cbs": "Statistieken over mensen met een beperking",
    "knowledge.sources.specification": "Volledige specificatie",
    "knowledge.sources.norm": "Wat is de norm?",

    "nav.knowledge.tools": "Tools",
    "nav.knowledge.checklist": "Checklist",
    "nav.knowledge.mistakes": "Veelgemaakte fouten",

    // Tools Page
    "knowledge.tools.title": "Toegankelijkheidstools",
    "knowledge.tools.intro":
      "Er zijn veel gratis en betaalde tools beschikbaar om de toegankelijkheid van jouw website te testen. Ontdek welke tools je kunt gebruiken.",
    "knowledge.tools.why.title": "Waarom tools gebruiken?",
    "knowledge.tools.why.text":
      "Toegankelijkheidstools helpen bij het identificeren van problemen die moeilijk met het blote oog te zien zijn. Ze automatiseren delen van het testproces en geven inzicht in technische issues. Echter, tools vinden slechts 30-40% van alle toegankelijkheidsproblemen - handmatige tests blijven essentieel.",
    "knowledge.tools.popular.title": "Populaire tools",
    "knowledge.tools.wave.title": "WAVE",
    "knowledge.tools.wave.desc":
      "Gratis browser extensie die visueel feedback geeft over toegankelijkheidsproblemen direct op de pagina.",
    "knowledge.tools.axe.title": "axe DevTools",
    "knowledge.tools.axe.desc":
      "Krachtige browser extensie voor ontwikkelaars met gedetailleerde technische rapportages en suggesties.",
    "knowledge.tools.lighthouse.title": "Lighthouse",
    "knowledge.tools.lighthouse.desc":
      "Ingebouwde Chrome tool die naast performance ook toegankelijkheid test en een score geeft.",
    "knowledge.tools.validator.title": "W3C Validator",
    "knowledge.tools.validator.desc":
      "Controleert of je HTML valide is, wat een basis vormt voor goede toegankelijkheid.",
    "knowledge.tools.nvda.title": "NVDA",
    "knowledge.tools.nvda.desc":
      "Gratis schermlezer voor Windows waarmee je kunt testen hoe blinde gebruikers jouw site ervaren.",
    "knowledge.tools.contrast.title": "Contrast Checker",
    "knowledge.tools.contrast.desc": "Test of jouw kleurcombinaties voldoende contrast hebben voor leesbaarheid.",
    "knowledge.tools.manual.title": "Handmatig testen",
    "knowledge.tools.manual.text":
      "Tools zijn waardevol maar niet voldoende. Test altijd ook handmatig: navigeer met alleen het toetsenbord, test met een schermlezer, en vraag gebruikers met een beperking om feedback.",
    "knowledge.tools.professional.title": "Professionele audit",
    "knowledge.tools.professional.text":
      "Voor een volledig beeld van de toegankelijkheid van jouw website is een professionele audit nodig. Experts combineren geautomatiseerde tests met handmatige controles en beoordelen ook gebruikerservaring en compliance met wetgeving.",
    "knowledge.tools.related.title": "Lees ook",
    "knowledge.tools.link1": "Toegankelijkheid checklist",
    "knowledge.tools.link2": "Veelgemaakte fouten",
    "knowledge.tools.link3": "De WCAG",

    // Checklist Page
    "knowledge.checklist.title": "Toegankelijkheid Checklist",
    "knowledge.checklist.intro":
      "Gebruik deze praktische checklist om de basistoegankelijkheid van jouw website te controleren. Dit is een startpunt - voor volledige compliance is een professionele audit nodig.",
    "knowledge.checklist.how.title": "Hoe deze checklist te gebruiken",
    "knowledge.checklist.how.text":
      "Loop elk onderdeel van jouw website na aan de hand van deze checkpunten. Markeer wat al goed is en noteer wat verbeterd moet worden. Deze checklist dekt de belangrijkste WCAG 2.2 Level A en AA criteria.",
    "knowledge.checklist.images.title": "Afbeeldingen",
    "knowledge.checklist.images.item1": "Alle informatieve afbeeldingen hebben een beschrijvende alt-tekst",
    "knowledge.checklist.images.item2": 'Decoratieve afbeeldingen hebben een lege alt-attribuut (alt="")',
    "knowledge.checklist.images.item3": "Complexe afbeeldingen hebben een uitgebreide beschrijving beschikbaar",
    "knowledge.checklist.text.title": "Tekst & Typografie",
    "knowledge.checklist.text.item1": "Tekst kan tot 200% vergroot worden zonder verlies van functionaliteit",
    "knowledge.checklist.text.item2": "Lettertype is leesbaar en minimaal 16px voor bodytekst",
    "knowledge.checklist.text.item3": "Tekstuitlijning is links (niet uitgevuld/justified)",
    "knowledge.checklist.navigation.title": "Navigatie",
    "knowledge.checklist.navigation.item1": "Alle functies zijn bereikbaar met alleen het toetsenbord",
    "knowledge.checklist.navigation.item2": "Er is een skip-link naar de hoofdinhoud",
    "knowledge.checklist.navigation.item3": "De navigatievolgorde is logisch en voorspelbaar",
    "knowledge.checklist.color.title": "Kleuren & Contrast",
    "knowledge.checklist.color.item1": "Tekstcontrast is minimaal 4.5:1 (kleine tekst) of 3:1 (grote tekst)",
    "knowledge.checklist.color.item2": "Informatie wordt niet alleen door kleur overgebracht",
    "knowledge.checklist.color.item3": "Focusindicatoren zijn duidelijk zichtbaar",
    "knowledge.checklist.media.title": "Multimedia",
    "knowledge.checklist.media.item1": "Video's hebben ondertiteling",
    "knowledge.checklist.media.item2": "Audio-content heeft een transcript",
    "knowledge.checklist.media.item3": "Geen automatisch afspelende media met geluid",
    "knowledge.checklist.forms.title": "Formulieren",
    "knowledge.checklist.forms.item1": "Alle formuliervelden hebben zichtbare labels",
    "knowledge.checklist.forms.item2": "Foutmeldingen zijn duidelijk en beschrijvend",
    "knowledge.checklist.forms.item3": "Verplichte velden zijn duidelijk aangegeven",
    "knowledge.checklist.note.title": "Belangrijk",
    "knowledge.checklist.note.text":
      "Deze checklist is een startpunt en dekt niet alle WCAG-criteria. Voor volledige compliance en een toegankelijkheidsverklaring is een professionele audit noodzakelijk. Neem contact met ons op voor een grondige analyse.",
    "knowledge.checklist.related.title": "Lees ook",
    "knowledge.checklist.link1": "Toegankelijkheidstools",
    "knowledge.checklist.link2": "Veelgemaakte fouten",
    "knowledge.checklist.link3": "De WCAG",

    // Common Mistakes Page
    "knowledge.mistakes.title": "Veelgemaakte Fouten",
    "knowledge.mistakes.intro":
      "Ontdek de meest voorkomende toegankelijkheidsfouten op websites en leer hoe u ze kunt vermijden of oplossen.",
    "knowledge.mistakes.why.title": "Waarom fouten voorkomen",
    "knowledge.mistakes.why.text":
      "Toegankelijkheidsfouten ontstaan vaak door gebrek aan kennis, tijdsdruk of het niet meenemen van toegankelijkheid in het ontwikkelproces. Door de meest voorkomende fouten te kennen, kun je ze proactief vermijden.",
    "knowledge.mistakes.alt.title": "Ontbrekende of slechte alt-teksten",
    "knowledge.mistakes.alt.desc":
      'Afbeeldingen zonder alt-tekst of met nietszeggende beschrijvingen zoals "afbeelding" of "foto" zijn onbruikbaar voor schermlezers.',
    "knowledge.mistakes.alt.solution":
      'Oplossing: Beschrijf wat de afbeelding toont of communiceert. Decoratieve afbeeldingen krijgen alt="".',
    "knowledge.mistakes.contrast.title": "Onvoldoende kleurcontrast",
    "knowledge.mistakes.contrast.desc":
      "Lichtgrijze tekst op wit of subtiele kleurcombinaties maken tekst moeilijk leesbaar voor veel mensen.",
    "knowledge.mistakes.contrast.solution":
      "Oplossing: Gebruik een contrastchecker om te zorgen voor minimaal 4.5:1 contrast ratio.",
    "knowledge.mistakes.keyboard.title": "Geen toetsenbordtoegankelijkheid",
    "knowledge.mistakes.keyboard.desc":
      "Interactieve elementen die alleen met de muis werken sluiten gebruikers uit die geen muis kunnen gebruiken.",
    "knowledge.mistakes.keyboard.solution":
      "Oplossing: Test alle functionaliteit met alleen Tab, Enter en pijltjestoetsen.",
    "knowledge.mistakes.labels.title": "Formulieren zonder labels",
    "knowledge.mistakes.labels.desc": "Invoervelden zonder gekoppelde labels zijn onbegrijpelijk voor schermlezers.",
    "knowledge.mistakes.labels.solution":
      "Oplossing: Gebruik <label> elementen die gekoppeld zijn aan invoervelden via for/id.",
    "knowledge.mistakes.headings.title": "Incorrecte koppenstructuur",
    "knowledge.mistakes.headings.desc":
      "Koppen die alleen voor styling worden gebruikt of die niveaus overslaan verstoren de paginavolgorde.",
    "knowledge.mistakes.headings.solution":
      "Oplossing: Gebruik koppen (h1-h6) in logische volgorde om de pagina te structureren.",
    "knowledge.mistakes.focus.title": "Onzichtbare focusindicator",
    "knowledge.mistakes.focus.desc":
      "Het verwijderen van de standaard focus-outline maakt toetsenbordnavigatie onmogelijk.",
    "knowledge.mistakes.focus.solution":
      "Oplossing: Behoud of verbeter de focusindicator, verwijder hem nooit volledig.",
    "knowledge.mistakes.links.title": "Onduidelijke linkteksten",
    "knowledge.mistakes.links.desc":
      'Links met tekst zoals "klik hier" of "lees meer" geven geen context buiten de zin.',
    "knowledge.mistakes.links.solution": "Oplossing: Gebruik beschrijvende linktekst die de bestemming aangeeft.",
    "knowledge.mistakes.language.title": "Geen taalattribuut",
    "knowledge.mistakes.language.desc":
      "Zonder lang-attribuut weten schermlezers niet met welke uitspraak ze moeten voorlezen.",
    "knowledge.mistakes.language.solution": 'Oplossing: Voeg lang="nl" toe aan het <html> element.',
    "knowledge.mistakes.prevention.title": "Fouten voorkomen",
    "knowledge.mistakes.prevention.text":
      "De beste manier om toegankelijkheidsfouten te voorkomen is door toegankelijkheid vanaf het begin mee te nemen in het ontwerp- en ontwikkelproces. Train je team, test regelmatig, en vraag feedback van gebruikers met een beperking.",
    "knowledge.mistakes.related.title": "Lees ook",
    "knowledge.mistakes.link1": "Toegankelijkheid checklist",
    "knowledge.mistakes.link2": "Toegankelijkheidstools",
    "knowledge.mistakes.link3": "De WCAG",

    // Quickscan Request Page
    "quickscan.request.pageTitle": "Quickscan aanvragen",
    "quickscan.request.step1.label": "Info",
    "quickscan.request.step2.label": "Persoonlijk",
    "quickscan.request.step3.label": "Bedrijf",
    "quickscan.request.step4.label": "Bevestig",
    "quickscan.request.step1.title": "De quickscan",
    "quickscan.request.step1.subtitle":
      "Een snelle, laagdrempelige scan van jouw website die de belangrijkste toegankelijkheidsproblemen in kaart brengt.",
    "quickscan.request.step1.whatYouGet": "Wat krijg je?",
    "quickscan.request.step1.feature1": "Een overzicht van de grootste toegankelijkheidsknelpunten op jouw website",
    "quickscan.request.step1.feature2": "Heldere prioritering van de aandachtspunten",
    "quickscan.request.step1.feature3": "Praktische aanbevelingen om direct mee aan de slag te gaan",
    "quickscan.request.step1.feature4": "Inzicht in je huidige WCAG 2.2 compliance niveau",
    "quickscan.request.step1.feature5": "Een uitstekend startpunt voor verdere toegankelijkheidsverbeteringen",
    "quickscan.request.step1.delivery": "Binnen 24-48 uur",
    "quickscan.request.step1.report": "Helder rapport",
    "quickscan.request.step1.actionable": "Direct toepasbaar",
    "quickscan.request.step1.priceNote": "Exclusief btw",
    "quickscan.request.step2.title": "Jouw gegevens",
    "quickscan.request.step2.subtitle": "Vul je persoonlijke gegevens in zodat we contact met je kunnen opnemen.",
    "quickscan.request.step3.title": "Bedrijfsgegevens",
    "quickscan.request.step3.subtitle": "Vul de gegevens van je organisatie in.",
    "quickscan.request.step4.title": "Controleer je aanvraag",
    "quickscan.request.step4.subtitle": "Controleer de ingevulde gegevens en bevestig je aanvraag.",
    "quickscan.request.step4.personalInfo": "Persoonlijke gegevens",
    "quickscan.request.step4.companyInfo": "Bedrijfsgegevens",
    "quickscan.request.step4.declarations": "Verklaringen",
    "quickscan.request.step4.agreement":
      "Ik ga akkoord met de kosten van €395 exclusief btw voor de QuickScan. Na bevestiging ontvang ik een factuur en wordt de scan binnen 24-48 uur na betaling uitgevoerd.",
    "quickscan.request.step4.termsAgreement": "Ik ga akkoord met de",
    "quickscan.request.step4.termsLink": "algemene voorwaarden",
    "quickscan.request.step4.and": "en",
    "quickscan.request.step4.privacyLink": "privacyverklaring",
    "quickscan.request.step4.marketingOptIn":
      "Ik ontvang graag nieuws, tips en updates over digitale toegankelijkheid per e-mail.",
    "quickscan.request.validation.terms": "Je moet akkoord gaan met de algemene voorwaarden om door te gaan.",
    "quickscan.request.form.name": "Naam",
    "quickscan.request.form.function": "Functie",
    "quickscan.request.form.phone": "Telefoonnummer",
    "quickscan.request.form.email": "E-mailadres",
    "quickscan.request.form.website": "Website URL",
    "quickscan.request.form.companyName": "Bedrijfsnaam",
    "quickscan.request.form.companyNameHint": "Zoals ingeschreven bij de KvK",
    "quickscan.request.form.street": "Straat + huisnummer",
    "quickscan.request.form.postalCode": "Postcode",
    "quickscan.request.form.city": "Plaats",
    "quickscan.request.form.country": "Land",
    "quickscan.request.form.vatNumber": "BTW-nummer",
    "quickscan.request.form.address": "Adres",
    "quickscan.request.back": "Vorige",
    "quickscan.request.next": "Volgende",
    "quickscan.request.submit": "Aanvraag versturen",
    "quickscan.request.validation.error": "Controleer de gegevens",
    "quickscan.request.validation.personal": "Vul minimaal je naam en e-mailadres in.",
    "quickscan.request.validation.company": "Vul minimaal je website, bedrijfsnaam, straat, postcode en plaats in.",
    "quickscan.request.validation.agreement": "Je dient akkoord te gaan met de kosten om de aanvraag te versturen.",
    "quickscan.request.validation.nameRequired": "Naam is verplicht.",
    "quickscan.request.validation.emailRequired": "E-mailadres is verplicht.",
    "quickscan.request.validation.emailInvalid": "Voer een geldig e-mailadres in.",
    "quickscan.request.validation.websiteRequired": "Website is verplicht.",
    "quickscan.request.validation.companyNameRequired": "Bedrijfsnaam is verplicht.",
    "quickscan.request.validation.streetRequired": "Straat en huisnummer is verplicht.",
    "quickscan.request.validation.postalCodeRequired": "Postcode is verplicht.",
    "quickscan.request.validation.cityRequired": "Plaats is verplicht.",
    "quickscan.request.success.title": "Aanvraag ontvangen!",
    "quickscan.request.success.description": "Bedankt voor je aanvraag. We nemen zo snel mogelijk contact met je op.",
    "quickscan.request.success.screen.title": "Je aanvraag is ontvangen!",
    "quickscan.request.success.screen.subtitle":
      "Bedankt voor je interesse in onze QuickScan. We gaan direct voor je aan de slag.",
    "quickscan.request.success.screen.emailTitle": "Bevestiging per e-mail",
    "quickscan.request.success.screen.emailDescription":
      "Je ontvangt binnen enkele minuten een bevestigingsmail op het opgegeven e-mailadres.",
    "quickscan.request.success.screen.nextSteps": "Wat gebeurt er nu?",
    "quickscan.request.success.screen.step1": "We bekijken je aanvraag en website",
    "quickscan.request.success.screen.step2": "Binnen 5 werkdagen ontvang je jouw QuickScan rapport",
    "quickscan.request.success.screen.step3": "Je ontvangt een factuur voor €395 excl. BTW",
    "quickscan.request.success.screen.newRequest": "Nieuwe aanvraag",
    "quickscan.request.success.screen.backHome": "Terug naar home",
    "quickscan.request.error.title": "Er ging iets mis",
    "quickscan.request.error.description": "Je aanvraag kon niet worden verzonden. Probeer het later opnieuw.",
    "quickscan.request.submitting": "Verzenden...",

    // Booking Request Page
    "booking.request.pageTitle": "Belafspraak maken",
    "booking.request.step1.label": "Datum & Tijd",
    "booking.request.step2.label": "Gegevens",
    "booking.request.step3.label": "Onderwerp",
    "booking.request.step4.label": "Samenvatting",
    "booking.request.step1.title": "Kies een datum en tijdvak",
    "booking.request.step1.subtitle": "Selecteer je gewenste moment voor een belafspraak.",
    "booking.request.step1.selectDate": "Kies een datum",
    "booking.request.step1.selectTime": "Kies een tijdvak",
    "booking.request.step1.selected": "Je keuze",
    "booking.request.step1.at": "om",
    "booking.request.step2.title": "Jouw gegevens",
    "booking.request.step2.subtitle": "Vul jouw contactgegevens in zodat we je kunnen bereiken.",
    "booking.request.step3.title": "Onderwerp van het gesprek",
    "booking.request.step3.subtitle": "Beschrijf kort waar je het over wil hebben.",
    "booking.request.step4.title": "Controleer je gegevens",
    "booking.request.step4.subtitle": "Bekijk de ingevulde informatie en bevestig je aanvraag.",
    "booking.request.step4.dateTime": "Datum en tijd",
    "booking.request.step4.personalInfo": "Jouw gegevens",
    "booking.request.step4.topic": "Onderwerp",
    "booking.request.form.name": "Naam",
    "booking.request.form.companyName": "Bedrijfsnaam",
    "booking.request.form.jobTitle": "Functie",
    "booking.request.form.phone": "Telefoonnummer",
    "booking.request.form.email": "E-mailadres",
    "booking.request.form.emailHint": "Optioneel - voor bevestiging per e-mail",
    "booking.request.form.topic": "Vraag of onderwerp",
    "booking.request.form.topicPlaceholder":
      "Waar kunnen we je mee helpen? Beschrijf kort je vraag of het onderwerp...",
    "booking.request.back": "Vorige",
    "booking.request.next": "Volgende",
    "booking.request.submit": "Afspraak bevestigen",
    "booking.request.submitting": "Verzenden...",
    "booking.request.validation.error": "Controleer de gegevens",
    "booking.request.validation.dateTime": "Selecteer een datum en tijdvak.",
    "booking.request.validation.personal": "Vul minimaal je naam, bedrijfsnaam en telefoonnummer in.",
    "booking.request.validation.topic": "Beschrijf kort het onderwerp van het gesprek.",
    "booking.request.success.title": "Je afspraak is aangevraagd!",
    "booking.request.success.subtitle": "Bedankt voor je aanvraag. We nemen zo snel mogelijk contact met je op.",
    "booking.request.success.emailTitle": "Bevestiging per e-mail",
    "booking.request.success.emailDescription":
      "Je ontvangt binnen enkele minuten een bevestigingsmail op het opgegeven e-mailadres.",
    "booking.request.success.nextSteps": "Wat gebeurt er nu?",
    "booking.request.success.step1": "We bekijken je aanvraag en nemen contact met je op",
    "booking.request.success.step2": "We bevestigen de afspraak telefonisch of per e-mail",
    "booking.request.success.step3": "Op de afgesproken datum en tijd bellen wij je",
    "booking.request.success.newRequest": "Nieuwe afspraak maken",
    "booking.request.success.backHome": "Terug naar home",
    "booking.request.error.title": "Er ging iets mis",
    "booking.request.error.description": "Je aanvraag kon niet worden verzonden. Probeer het later opnieuw.",

    // Why Section (new combined section)
    "why.title": "Waarom toegankelijkheid?",
    "why.subtitle": "Iedereen verdient toegang tot jouw website. Wij helpen je dat waar te maken.",
    "why.description":
      "De EU toegankelijkheidswet komt eraan. Maar toegankelijkheid is meer dan compliance, het is de juiste keuze voor je gebruikers én je bedrijf.",
    "why.pillar.1.title": "Helder, geen jargon",
    "why.pillar.1.description":
      "Wij vertalen WCAG-richtlijnen naar begrijpelijke taal en concrete acties die jouw team direct kan toepassen.",
    "why.pillar.1.link": "Bekijk onze werkwijze",
    "why.pillar.2.title": "Samen, niet alleen",
    "why.pillar.2.description":
      "We werken samen met jouw team aan duidelijke en haalbare verbeteringen. Met praktisch advies en gerichte ondersteuning helpen we stap voor stap vooruit.",
    "why.pillar.2.link": "Meer over ons",
    "why.pillar.3.title": "Blijvend compliant",
    "why.pillar.3.description":
      "Doorlopende monitoring zorgt ervoor dat jouw website toegankelijk blijft, niet één keer maar altijd.",
    "why.pillar.3.link": "Bekijk onze diensten",

    // Journey Preview (simplified process)
    "journey.title": "Hoe wij werken",
    "journey.subtitle": "Van eerste gesprek tot blijvende toegankelijkheid",
    "journey.step.1.title": "Kennismaken",
    "journey.step.1.desc":
      "We luisteren naar jouw situatie en bespreken je doelen. Geen verkooppraatje, gewoon een eerlijk gesprek.",
    "journey.step.2.title": "Analyseren",
    "journey.step.2.desc":
      "We onderzoeken jouw website grondig en brengen de toegankelijkheid in kaart met heldere rapportages.",
    "journey.step.3.title": "Begeleiden",
    "journey.step.3.desc": "We helpen bij het implementeren van verbeteringen en zorgen voor blijvende compliance.",
    "journey.cta": "Bekijk onze volledige werkwijze",

    // Who Section
    "who.label": "EU toegankelijkheidswet",
    "who.title": "Geldt dit ook voor jouw bedrijf?",
    "who.description":
      "De Europese toegankelijkheidswet is van kracht sinds 28 juni 2025. Bekijk of jouw sector eronder valt en wat dit voor jou betekent.",
    "who.stats.nl.percentage": "25%",
    "who.stats.nl.label": "van de Nederlanders heeft een functionele beperking, mogelijk ook jouw klanten",
    "who.stats.eu.percentage": "87 miljoen",
    "who.stats.eu.label": "mensen in de EU met een beperking kunnen nu toegang tot jouw dienst verwachten",
    "who.stats.websites.percentage": "96%",
    "who.stats.websites.label": "van websites is nog niet toegankelijk, hoort jouw site daarbij?",
    "who.moreInfo": "Ontdek meer",
    "who.sectors.examples": "Herken je dit?",
    "who.sectors.requirement":
      "Werk je in deze sector? Dan moet jouw website voldoen aan de WCAG 2.2 richtlijnen. Wij helpen je graag op weg.",
    "who.sectors.ecommerce": "Webshops & e-commerce",
    "who.sectors.ecommerce.intro":
      "Verkoop je producten online? Dan moet jouw webshop toegankelijk zijn voor iedereen ook voor klanten die blind zijn, slechthorend of moeite hebben met scrollen.",
    "who.sectors.ecommerce.example1": "Je hebt een webshop met productpagina's en een betaalproces",
    "who.sectors.ecommerce.example2": "Klanten kunnen online reserveren of bestellen",
    "who.sectors.ecommerce.example3": "Je biedt een platform waar anderen hun producten verkopen",
    "who.sectors.banking": "Banken & financiële diensten",
    "who.sectors.banking.intro":
      "Bied je financiële diensten aan? Van online bankieren tot verzekeringen jouw digitale kanalen moeten voor iedereen bruikbaar zijn.",
    "who.sectors.banking.example1": "Klanten kunnen online bankieren of een app gebruiken",
    "who.sectors.banking.example2": "Je biedt verzekeringen, hypotheken of pensioendiensten aan",
    "who.sectors.banking.example3": "Je hebt pinautomaten of betaalterminals",
    "who.sectors.healthcare": "Zorg & gezondheidsdiensten",
    "who.sectors.healthcare.intro":
      "Werk je in de zorg? Patiënten moeten ook digitaal goed geholpen kunnen worden denk aan portalen, afspraken maken of dossiers inzien.",
    "who.sectors.healthcare.example1": "Patiënten plannen online afspraken via jouw portal",
    "who.sectors.healthcare.example2": "Je biedt digitale gezondheidsdossiers aan",
    "who.sectors.healthcare.example3": "Je werkt met e-health apps of videoconsulten",
    "who.sectors.government": "Overheid & publieke sector",
    "who.sectors.government.intro":
      "Overheidsorganisaties moesten al langer toegankelijk zijn. Werk jij voor een gemeente, uitvoeringsinstantie of publieke organisatie? Controleer of je nog voldoet.",
    "who.sectors.government.example1": "Je beheert een gemeentelijke website of DigiD-koppeling",
    "who.sectors.government.example2": "Burgers kunnen aanvragen doen of formulieren invullen",
    "who.sectors.government.example3": "Je biedt publieke informatie of nieuwsberichten aan",
    "who.sectors.telecom": "Telecom & media",
    "who.sectors.telecom.intro":
      "Stream je video's, bied je e-books aan of lever je telecomdiensten? Dan valt jouw platform onder de wet.",
    "who.sectors.telecom.example1": "Je biedt streaming zoals video of muziek aan",
    "who.sectors.telecom.example2": "Je levert digitale televisie of interactieve diensten",
    "who.sectors.telecom.example3": "Klanten kunnen e-books lezen via jouw platform",
    "who.sectors.transport": "Transport & reizen",
    "who.sectors.transport.intro":
      "Verkoop je treinkaartjes, vliegtickets of bied je een reisplanner aan? Dan moeten jouw digitale systemen toegankelijk zijn.",
    "who.sectors.transport.example1": "Reizigers boeken online trein-, bus- of vliegtickets",
    "who.sectors.transport.example2": "Je hebt kaartjesautomaten of een app voor reisinformatie",
    "who.sectors.transport.example3": "Je biedt een reisplanner of OV-informatie aan",
    "who.deadline.note":
      "De EU toegankelijkheidswet is van kracht sinds 28 juni 2025. Voldoet jouw website al? Wij helpen je graag.",
  },
  en: {
    // Navbar
    "nav.services": "Services",
    "nav.approach": "Approach",
    "nav.about": "About Us",
    "nav.contact": "Contact",
    "nav.skipToMain": "Skip to main content",

    // About Page
    "about.title": "About Us",
    "about.intro.p1":
      "We are a growing professional agency dedicated to helping organizations meet and maintain digital accessibility standards. Our approach is clear, structured and collaborative. We focus on creating practical insights that teams can immediately apply, without unnecessary complexity or technical jargon.",
    "about.intro.p2":
      "EqualAccess was founded on a clear principle: digital accessibility should be understandable, achievable and sustainable for every organization. Many organizations recongnize the importance of accessibility but struggle with the complexity of WCAG 2.2. The guidelines are technical, the process can be unclear and finding practical solutions often requires more than a checklist.",
    "about.intro.p3": "That is where EqualAccess provides guidance.",
    "about.intro.p4":
      "Each audit we perform delivers more than compliance data. We translate findings into clear actions, helping businesses improve accessibility step by step. Together with our clients who want to stay compliant over time, we offer annual programs that include regular re-audits, updates and ongoing support. Because websites evolve and accessibility should evolve with them.",
    "about.intro.p5":
      "EqualsAccess works with small and medium enterprises, public institutions, larger organizatoins and digital agencies. Our clients share a common goal: to create digital experiences that are open and usable for everyone.",
    "about.closing": "EqualAccess - Your accessibility, our guarantee.",

    // Hero
    "hero.title": "Your website, our guarantee!",
    "hero.subtitle": "We help you make your website accessible not once, but always.",
    "hero.description": "Discover how we take your digital accessibility to the next level with proven expertise and sustainable solutions.",
    "hero.cta.primary": "Free Consultation",
    "hero.cta.quickscan": "Request a QuickScan",
    "hero.cta.secondary": "Read More",
    "hero.benefit.1": "Clear language, real expertise",
    "hero.benefit.2": "Continuous monitoring",
    "hero.benefit.3": "Together towards results",

    // Problem
    "problem.title": "Together Towards Accessibility",
    "problem.intro":
      "Most companies want to do the right thing. But accessibility can feel complicated. Especially when you don't know exactly where to start. Sound familiar?",
    "problem.challenge.1.title": "WCAG rules feel like a technical wall",
    "problem.challenge.1.desc": "We translate them into concrete steps understandable for everyone.",
    "problem.challenge.2.title": "Audits are often just a list of errors",
    "problem.challenge.2.desc": "We work together with you, without pointing fingers but with clear advice.",
    "problem.challenge.3.title": "Deadlines are approaching",
    "problem.challenge.3.desc":
      "Together we ensure you're compliant on time with a plan that works for your situation.",
    "problem.closing": "Together we make accessibility understandable, achievable and sustainable.",

    // Why Section (new combined section)
    "why.title": "Why Accessibility?",
    "why.subtitle": "Everyone deserves access to your website. We help you make that happen.",
    "why.description":
      "The EU Accessibility Act is coming. But accessibility is more than compliance — it's the right choice for your users and your business.",
    "why.pillar.1.title": "Clear, no jargon",
    "why.pillar.1.description":
      "We translate WCAG guidelines into clear language and concrete actions your team can apply directly.",
    "why.pillar.1.link": "View our approach",
    "why.pillar.2.title": "Together, not alone",
    "why.pillar.2.description":
      "We work closely with your team. No finger-pointing, just practical advice and support.",
    "why.pillar.2.link": "More about us",
    "why.pillar.3.title": "Continuously compliant",
    "why.pillar.3.description": "Ongoing monitoring ensures your website stays accessible, not once but always.",
    "why.pillar.3.link": "View our services",

    // Journey Preview (simplified process)
    "journey.title": "How We Work",
    "journey.subtitle": "From first conversation to lasting accessibility",
    "journey.step.1.title": "Meet",
    "journey.step.1.desc":
      "We listen to your situation and discuss your goals. No sales pitch, just an honest conversation.",
    "journey.step.2.title": "Analyze",
    "journey.step.2.desc": "We thoroughly examine your website and map out accessibility with clear reports.",
    "journey.step.3.title": "Guide",
    "journey.step.3.desc": "We help implement improvements and ensure lasting compliance.",
    "journey.cta": "View our full approach",

    // Who Section
    "who.label": "EU Accessibility Act",
    "who.title": "Does this apply to your business?",
    "who.description":
      "The European Accessibility Act has been in effect since June 28, 2025. See if your sector is covered — and what it means for you.",
    "who.stats.nl.percentage": "25%",
    "who.stats.nl.label": "of Dutch people have a functional disability — possibly your customers too",
    "who.stats.eu.percentage": "87 million",
    "who.stats.eu.label": "people in the EU with disabilities can now expect access to your service",
    "who.stats.websites.percentage": "96%",
    "who.stats.websites.label": "of websites are not yet accessible — is yours one of them?",
    "who.moreInfo": "Learn more",
    "who.sectors.examples": "Does this sound familiar?",
    "who.sectors.requirement":
      "Work in this sector? Your website must comply with WCAG 2.2 guidelines. We're happy to help you get started.",
    "who.sectors.ecommerce": "Webshops & E-commerce",
    "who.sectors.ecommerce.intro":
      "Do you sell products online? Your webshop must be accessible to everyone — including customers who are blind, hard of hearing, or have difficulty scrolling.",
    "who.sectors.ecommerce.example1": "You have a webshop with product pages and a checkout process",
    "who.sectors.ecommerce.example2": "Customers can reserve or order online",
    "who.sectors.ecommerce.example3": "You provide a platform where others sell their products",
    "who.sectors.banking": "Banking & Financial services",
    "who.sectors.banking.intro":
      "Do you offer financial services? From online banking to insurance — your digital channels must be usable by everyone.",
    "who.sectors.banking.example1": "Customers can bank online or use an app",
    "who.sectors.banking.example2": "You offer insurance, mortgages, or pension services",
    "who.sectors.banking.example3": "You have ATMs or payment terminals",
    "who.sectors.healthcare": "Healthcare services",
    "who.sectors.healthcare.intro":
      "Work in healthcare? Patients should be able to access care digitally too — think portals, appointments, and medical records.",
    "who.sectors.healthcare.example1": "Patients schedule appointments online through your portal",
    "who.sectors.healthcare.example2": "You offer digital health records",
    "who.sectors.healthcare.example3": "You work with e-health apps or video consultations",
    "who.sectors.government": "Government & Public sector",
    "who.sectors.government.intro":
      "Government organizations have long been required to be accessible. Work for a municipality or public agency? Check if you're still compliant.",
    "who.sectors.government.example1": "You manage a municipal website or digital ID integration",
    "who.sectors.government.example2": "Citizens can submit applications or fill out forms",
    "who.sectors.government.example3": "You provide public information or news",
    "who.sectors.telecom": "Telecom & Media",
    "who.sectors.telecom.intro":
      "Stream videos, offer e-books, or provide telecom services? Your platform falls under the law.",
    "who.sectors.telecom.example1": "You offer streaming like video or music",
    "who.sectors.telecom.example2": "You provide digital television or interactive services",
    "who.sectors.telecom.example3": "Customers can read e-books through your platform",
    "who.sectors.transport": "Transport & Travel",
    "who.sectors.transport.intro":
      "Sell train tickets, flights, or offer a travel planner? Your digital systems must be accessible.",
    "who.sectors.transport.example1": "Travelers book train, bus, or flight tickets online",
    "who.sectors.transport.example2": "You have ticket machines or a travel info app",
    "who.sectors.transport.example3": "You offer a travel planner or public transport info",
    "who.deadline.note":
      "The EU Accessibility Act has been in effect since June 28, 2025. Is your website compliant? We're happy to help.",
    "solution.subtitle": "Accessibility Done Right",
    "solution.title": "Why EqualAccess",
    "solution.intro":
      "We guide you step by step. With understanding, clarity and expertise. So accessibility isn't a burden, but a positive step forward.",
    "solution.usp.1.title": "We make accessibility understandable",
    "solution.usp.1.desc":
      "Not a technical wall, but a clear path. We translate WCAG into plain language and concrete actions that truly work.",
    "solution.usp.2.title": "We work with you, not against you",
    "solution.usp.2.desc": "We collaborate with your team to realize improvements.",
    "solution.usp.3.title": "Continuously compliant, with everthing in control",
    "solution.usp.3.desc":
      "A website constantly changes. That's why we offer continuous monitoring, so you don't have to start over each year.",
    "solution.usp.4.title": "Independent and human",
    "solution.usp.4.desc":
      "We don't sell expensive implementation packages. We give honest advice, focused on what your business truly needs.",
    "solution.usp.5.title": "Expertise with commitment",
    "solution.usp.5.desc":
      "We understand the technical side, but also the social value: equal access for everyone. Because it matters.",

    // Process
    "process.title": "Together Towards Accessibility",
    "process.intro": "In four clear steps, we bring your website to WCAG 2.2 level explained clearly, guided humanly.",
    "process.step.1.title": "Introduction",
    "process.step.1.desc":
      "We listen to your situation and ambitions. What's your question? Where do you want to go? We take the time.",
    "process.step.2.title": "Scan & Analysis",
    "process.step.2.desc":
      "We screen your website for accessibility. Clear, organized and without jargon you know exactly where you stand.",
    "process.step.3.title": "Tailored Advice",
    "process.step.3.desc":
      "You get concrete improvement steps not a laundry list, but a workable plan. Including priorities and timelines.",
    "process.step.4.title": "Monitoring & Support",
    "process.step.4.desc": "We keep watching. Continuously. So your website stays accessible not once, but always.",

    // CTA
    "cta.title": "Shall We Take a Look Together?",
    "cta.subtitle": "Schedule a free consultation. We're happy to think along with you.",
    "cta.feature.1": "Explained clearly and",
    "cta.feature.2": "Honest advice",
    "cta.feature.3": "Quick response,",
    "cta.button": "Schedule a Call",

    // Footer
    "footer.tagline": "Towards accessibility together. Clear, human and expert.",
    "footer.links.title": "Quick Links",
    "footer.links.about": "About Us",
    "footer.links.services": "Services",
    "footer.links.approach": "Approach",
    "footer.links.booking": "Schedule a Call",
    "footer.contact.title": "Contact",
    "footer.company.title": "Company Details",
    "footer.company.name": "EqualAccess B.V.",
    "footer.company.kvk": "Chamber of Commerce number: 98044907",
    "footer.company.btw": "VAT-number: NL868336853B01",
    "footer.legal.title": "Legal",
    "footer.legal.privacy": "Privacy Policy",
    "footer.legal.terms": "Terms & Conditions",
    "footer.legal.accessibility": "Accessibility Statement",
    "footer.copyright": "© 2025 EqualAccess B.V. All rights reserved.",

    // Contact Page
    "contact.title": "Contact Form",
    "contact.intro":
      "Got a question, want to know how accessible your website really is, or simply want to talk things through? We’re here to help — no hassle, no jargon. Leave us a message and we’ll get back to you within one business day.",
    "contact.info.title": "Get in touch",
    "contact.info.email": "Email",
    "contact.info.phone": "Phone",
    "contact.form.title": "Send a message",
    "contact.form.message": "Message",
    "contact.form.message.placeholder": "Tell us how we can help you...",
    "contact.form.firstName": "First Name",
    "contact.form.lastName": "Last Name",
    "contact.form.organization": "Organization",
    "contact.form.email": "Email Address",
    "contact.form.phone": "Phone Number",
    "contact.form.updates": "Send me the latest updates about digital accessibility.",
    "contact.form.submit": "Send My Message",
    "contact.form.required": "required",
    "contact.form.success": "Thank you for your message! We'll get back to you as soon as possible.",
    "contact.form.error": "Something went wrong. Please try again later.",
    "contact.form.validation.messageRequired": "Message is required",
    "contact.form.validation.firstNameRequired": "First name is required",
    "contact.form.validation.lastNameRequired": "Last name is required",
    "contact.form.validation.emailInvalid": "Invalid email address",
    "contact.form.validation.phoneInvalid": "Invalid phone number format",
    "contact.booking.title": "Book a call",
    "contact.booking.description": "Schedule a call directly in our calendar.",
    "contact.booking.formTitle": "Schedule an appointment",
    "contact.booking.selectDate": "Select a date",
    "contact.booking.selectTime": "Select a time",
    "contact.booking.pickDate": "Pick a date",
    "contact.booking.pickTime": "Pick a time",
    "contact.booking.name": "Name",
    "contact.booking.notes": "Notes",
    "contact.booking.notesPlaceholder": "How can we help you?",
    "contact.booking.submit": "Request appointment",
    "contact.booking.validation.nameRequired": "Name is required",
    "contact.booking.validation.phoneRequired": "Phone number is required",
    "contact.booking.validation.dateTimeRequired": "Please select a date and time",
    "contact.booking.error": "Something went wrong while scheduling the appointment.",
    "contact.booking.success.title": "Appointment requested!",
    "contact.booking.success.description": "We will contact you as soon as possible to confirm the appointment.",
    "contact.booking.success.newBooking": "Make another appointment",
    "contact.booking.fullFlow": "Full booking flow →",

    // Privacy
    "privacy.title": "Privacy Policy",
    "privacy.intro.title": "Introduction",
    "privacy.intro.text":
      "EqualAccess B.V. respects your privacy and handles your personal data with care. This privacy policy explains what data we collect, why we do so, and what your rights are.",
    "privacy.data.title": "What data do we collect?",
    "privacy.data.text": "We collect the following personal data when you contact us:",
    "privacy.data.item1": "Name and contact details (email address, phone number)",
    "privacy.data.item2": "Company name and organization details",
    "privacy.data.item3": "Communication via our contact form",
    "privacy.data.item4": "Technical data such as IP address and browser type (functional cookies only)",
    "privacy.purpose.title": "What do we use your data for?",
    "privacy.purpose.text": "We use your personal data exclusively for:",
    "privacy.purpose.item1": "Responding to your questions and requests",
    "privacy.purpose.item2": "Providing our web accessibility services",
    "privacy.purpose.item3": "Improving our website and services",
    "privacy.rights.title": "Your rights",
    "privacy.rights.text": "You have the right to:",
    "privacy.rights.item1": "Request access to your personal data",
    "privacy.rights.item2": "Have your data corrected or deleted",
    "privacy.rights.item3": "Object to the processing of your data",
    "privacy.rights.item4": "Withdraw your consent",
    "privacy.rights.item5": "File a complaint with the Dutch Data Protection Authority",
    "privacy.security.title": "Security",
    "privacy.security.text":
      "We take appropriate technical and organizational measures to protect your personal data against loss, misuse and unauthorized access.",
    "privacy.contact.title": "Contact",
    "privacy.contact.text": "For questions about this privacy policy or the use of your data, please contact us:",

    // Terms
    "terms.title": "Terms & Conditions",
    "terms.effectiveDate": "Effective date: 1 January 2026",

    // Article 1 - Definitions
    "terms.definitions.title": "Article 1. Definitions",
    "terms.definitions.b2b":
      "These Terms apply exclusively to business users (B2B). They do not apply to consumers. By using any EqualAccess service, the Client confirms acting in the course of business.",
    "terms.definitions.terms":
      'Terms and Conditions: these general terms and conditions of EqualAccess B.V., CoC 98044907, hereafter "EqualAccess".',
    "terms.definitions.day": "Day: calendar day.",
    "terms.definitions.continuous":
      "Continuous Agreement: any agreement involving recurring delivery of services over a defined period, including monitoring and maintenance agreements.",
    "terms.definitions.deliveryTerm":
      "Delivery Term: the timeframe indicated by EqualAccess for completion of the Performance or any part thereof.",
    "terms.definitions.contractor": 'Contractor: EqualAccess B.V., named "EqualAccess" throughout.',
    "terms.definitions.client":
      "Client: any natural or legal person negotiating with or entering into an Agreement with EqualAccess.",
    "terms.definitions.agreement":
      "Agreement: any agreement concluded between the Client and EqualAccess relating to the Performance.",
    "terms.definitions.performance":
      "Performance: the services or products provided by EqualAccess, including accessibility audits, consulting, monitoring, training, and related activities.",
    "terms.definitions.documentation":
      "Documentation: all manuals, specifications, descriptions, reports, technical documents, and any other materials provided by EqualAccess.",

    // Article 2 - Applicability
    "terms.applicability.title": "Article 2. Applicability",
    "terms.applicability.text1":
      "These Terms and Conditions apply to all activities conducted by EqualAccess, including but not limited to quotations, agreements, advisory services, communications, workshops, analyses, demonstrations, audits, monitoring, and any form of collaboration or interaction with the Client — regardless of whether such activities directly lead to a formal Agreement.",
    "terms.applicability.text2":
      "These Terms and Conditions also apply to any legal acts, preparatory work, pre-contractual discussions, negotiations, or exploratory engagements carried out by EqualAccess.",
    "terms.applicability.text3":
      "Any terms of the Client are expressly rejected unless accepted in writing by EqualAccess.",
    "terms.applicability.text4": "Deviations from these Terms and Conditions are valid only if agreed upon in writing.",

    // Article 3 - Quotations
    "terms.quotes.title": "Article 3. Quotations and Proposals",
    "terms.quotes.text1": "All offers by EqualAccess are non-binding unless explicitly stated otherwise.",
    "terms.quotes.text2":
      "Offers remain valid for the period specified therein; if no period is stated, a validity of fourteen days applies.",
    "terms.quotes.text3": "Obvious mistakes, calculation errors, and typographical errors do not bind EqualAccess.",
    "terms.quotes.text4": "Amendments to offers are binding only if confirmed in writing.",

    // Article 4 - Formation
    "terms.formation.title": "Article 4. Formation of the Agreement",
    "terms.formation.text1":
      "An Agreement is established when EqualAccess confirms the assignment in writing or electronically.",
    "terms.formation.text2":
      "If the Client modifies the offer, the Agreement is formed only after EqualAccess confirms the amended terms.",
    "terms.formation.text3":
      "If performance begins without written confirmation, the issued invoice is deemed to represent the Agreement in full.",

    // Article 5 - Continuous Agreements
    "terms.continuous.title": "Article 5. Continuous Agreements",
    "terms.continuous.text1": "Continuous Agreements apply for the agreed duration.",
    "terms.continuous.text2": "In the absence of clarity, the duration is deemed to be twelve months.",
    "terms.continuous.text3":
      "Continuous Agreements may not be terminated early without payment of remaining contractual amounts.",
    "terms.continuous.text4":
      "Upon expiry, the Agreement is tacitly renewed unless terminated in writing at least two months in advance.",

    // Article 6 - Obligations EqualAccess
    "terms.obligations.title": "Article 6. Obligations of EqualAccess",
    "terms.obligations.text1": "EqualAccess performs its services on a best-efforts basis.",
    "terms.obligations.text2":
      "Services are performed with due care, craftsmanship, and in accordance with agreed specifications.",
    "terms.obligations.text3":
      "EqualAccess may engage third parties in performing the Performance and may pass on associated costs.",
    "terms.obligations.text4":
      "Minor or non-essential adjustments may be made provided they do not affect core functionality.",
    "terms.obligations.text5":
      "EqualAccess implements appropriate technical and organisational security measures consistent with European data protection standards.",

    // Article 7 - Obligations Client
    "terms.clientObligations.title": "Article 7. Obligations of the Client",
    "terms.clientObligations.text1":
      "The Client shall provide all information required for proper execution of the Performance in a timely and accurate manner.",
    "terms.clientObligations.text2": "The Client warrants the accuracy and completeness of provided information.",
    "terms.clientObligations.text3": "Materials must be provided in a commonly accepted and usable format.",
    "terms.clientObligations.text4":
      "The Client guarantees that all supplied materials are free of third-party rights or that permission has been obtained.",
    "terms.clientObligations.text5":
      "The Client shall provide access to systems, environments, or infrastructure if required for execution.",

    // Article 8 - Performance and Acceptance
    "terms.acceptance.title": "Article 8. Performance and Acceptance",
    "terms.acceptance.text1": "EqualAccess may require written approval of specifications prior to commencement.",
    "terms.acceptance.text2":
      "If work is carried out in phases, commencement of a subsequent phase may be suspended until the previous one is approved.",
    "terms.acceptance.text3":
      "Acceptance occurs upon delivery, upon expiry of an agreed test period, or upon operational use by the Client.",
    "terms.acceptance.text4": "Minor defects not hindering operational use do not justify refusal of acceptance.",

    // Article 9 - Prices
    "terms.prices.title": "Article 9. Prices",
    "terms.prices.text1":
      "All prices are exclusive of VAT and other applicable taxes, unless expressly stated otherwise.",
    "terms.prices.text2":
      "Prices may be adjusted after three months due to circumstances such as inflation, legal changes, or increased costs.",
    "terms.prices.text3":
      "Adjustments due to changes in assignment qualify as additional work and will be billed separately.",

    // Article 10 - Delivery Terms
    "terms.delivery.title": "Article 10. Delivery Terms",
    "terms.delivery.text1": "Delivery terms are indicative unless expressly agreed otherwise.",
    "terms.delivery.text2": "Exceeding a delivery term does not entitle the Client to compensation or dissolution.",
    "terms.delivery.text3":
      "A Performance is considered delivered when EqualAccess notifies the Client and no objection is made within five days.",

    // Article 11 - Changes and Additional Work
    "terms.additionalWork.title": "Article 11. Changes and Additional Work",
    "terms.additionalWork.text1": "Any change in the assignment results in additional or reduced work.",
    "terms.additionalWork.text2": "Additional work is invoiced separately.",
    "terms.additionalWork.text3":
      "If circumstances require additional effort beyond initial expectations, EqualAccess may charge additional fees.",

    // Article 12 - Intellectual Property
    "terms.ip.title": "Article 12. Intellectual Property",
    "terms.ip.text1":
      "All intellectual property rights to developed reports, documentation, and advice remain the property of EqualAccess, unless explicitly agreed otherwise in writing.",
    "terms.ip.text2": "The Client receives a non-exclusive right to use the deliverables for the agreed purposes.",
    "terms.ip.text3": "The Client may not remove or circumvent technical safeguards or protection mechanisms.",

    // Article 13 - Payment
    "terms.payment.title": "Article 13. Payment Terms",
    "terms.payment.text1":
      "Invoices must be paid within fourteen days of issue, unless a different payment term is specified in the quotation.",
    "terms.payment.text2": "Statutory commercial interest applies in the event of late payment.",
    "terms.payment.text3": "All collection costs resulting from late or non-payment are borne by the Client.",

    // Article 14 - Suspension and Retention
    "terms.suspension.title": "Article 14. Suspension and Retention of Title",
    "terms.suspension.text1": "Deliveries remain the property of EqualAccess until payment has been made in full.",
    "terms.suspension.text2": "In the event of payment default, EqualAccess may suspend services.",

    // Article 15 - Warranty
    "terms.warranty.title": "Article 15. Warranty",
    "terms.warranty.text1":
      "EqualAccess provides a warranty period of thirty days (one month) starting from the date of Acceptance as defined in Article 8.",
    "terms.warranty.text2":
      "During this warranty period, EqualAccess shall remedy defects that prevent the services from functioning in accordance with the agreed specifications, provided such defects are reported in writing within the warranty period.",
    "terms.warranty.text3":
      "The warranty does not apply to: issues caused by third-party systems, integrations, environments, or changes outside EqualAccess's control; modifications made by the Client or third parties; defects arising from improper use, misuse, or use contrary to Documentation.",

    // Article 16 - Liability
    "terms.liability.title": "Article 16. Liability",
    "terms.liability.text1":
      "Liability of EqualAccess is limited to direct damages up to a maximum of the fees paid for three months of services, unless mandatory law prescribes otherwise.",
    "terms.liability.text2":
      "EqualAccess is never liable for indirect or consequential damages, including but not limited to loss of revenue, data loss, reputational damage, or business interruption.",
    "terms.liability.text3":
      "EqualAccess is not liable for any loss, suspension, downgrade, or withdrawal of certifications on the side of the Client, where such consequences arise from failures, outages, or conduct of third-party suppliers, hosting providers, infrastructure partners, or other external dependencies not under the direct control of EqualAccess.",

    // Article 17 - Force Majeure
    "terms.forceMajeure.title": "Article 17. Force Majeure",
    "terms.forceMajeure.text":
      "In the event of force majeure, EqualAccess may suspend or terminate its obligations without liability.",

    // Article 18 - Confidentiality
    "terms.confidentiality.title": "Article 18. Confidentiality",
    "terms.confidentiality.text1":
      "Both parties shall maintain strict confidentiality regarding all confidential information received. EqualAccess shall at all times exercise maximum effort to uphold confidentiality standards by ensuring the use of Non-Disclosure Agreements (NDAs) with all involved parties, including external suppliers, subcontractors, and third-party service providers engaged in the execution of the Agreement.",
    "terms.confidentiality.text2":
      "EqualAccess shall never disclose confidential information about Clients, their internal processes, systems, data, or project specifics to other Clients or external parties, unless legally required to do so.",
    "terms.confidentiality.text3":
      "Unless explicitly agreed otherwise in writing, EqualAccess is permitted to reference the Client as a customer for marketing purposes, including the display of the Client's logo on the EqualAccess website or within marketing materials. Upon written request from the Client, EqualAccess will remove such logo or references.",
    "terms.confidentiality.text4":
      "Confidentiality obligations remain in force during the Agreement and for five years after its termination.",

    // Article 19 - Non-Solicitation
    "terms.nonSolicitation.title": "Article 19. Non-Solicitation",
    "terms.nonSolicitation.text":
      "The Client may not hire or engage EqualAccess personnel during the Agreement and for five years thereafter.",

    // Article 20 - Amendments
    "terms.changes.title": "Article 20. Amendments",
    "terms.changes.text":
      "EqualAccess may amend these Terms and Conditions; amendments apply unless the Client objects in writing within thirty days.",

    // Article 21 - Complaints
    "terms.complaints.title": "Article 21. Complaints",
    "terms.complaints.text": "Complaints must be submitted in writing within fourteen days.",

    // Article 22 - Governing Law
    "terms.law.title": "Article 22. Governing Law and Disputes",
    "terms.law.text1": "Dutch law applies to all Agreements.",
    "terms.law.text2": "Disputes shall be submitted exclusively to the competent court in Amsterdam, the Netherlands.",

    // Article 23 - Entire Agreement
    "terms.entireAgreement.title": "Article 23. Entire Agreement",
    "terms.entireAgreement.text":
      "These Terms, together with any written agreement or quotation, constitute the entire agreement between the Parties and supersede all prior discussions, promises, or representations.",
    "terms.entireAgreement.disclaimer":
      "In case of any discrepancy between different language versions of these Terms and Conditions, the Dutch version shall prevail.",

    // Accessibility Statement
    "accessibility.title": "Accessibility Statement",
    "accessibility.commitment.title": "Our commitment",
    "accessibility.commitment.text":
      "EqualAccess B.V. strives to make this website accessible to everyone, regardless of disability or technology used. We want all visitors to be able to use and understand our website.",
    "accessibility.standards.title": "Accessibility standard",
    "accessibility.standards.text": "This website complies with WCAG 2.2 Level AA. This means that the website:",
    "accessibility.standards.item1": "Is fully operable with keyboard",
    "accessibility.standards.item2": "Works well with screen readers and other assistive technology",
    "accessibility.standards.item3": "Has sufficient color contrast for readability",
    "accessibility.standards.item4": "Has clear headings and a logical structure",
    "accessibility.measures.title": "Measures taken",
    "accessibility.measures.text": "We have taken the following measures to ensure accessibility:",
    "accessibility.measures.item1": "Accessibility is part of the design from the start",
    "accessibility.measures.item2": "Regular audits and tests with screen readers",
    "accessibility.measures.item3": "Clear focus indicators for keyboard navigation",
    "accessibility.measures.item4": "Alt texts for all images",
    "accessibility.measures.item5": "Semantic HTML and ARIA labels where needed",
    "accessibility.limitations.title": "Known limitations",
    "accessibility.limitations.text":
      "We continuously work on improving accessibility. If you still experience problems despite our efforts, we would like to hear about it.",
    "accessibility.feedback.title": "Feedback",
    "accessibility.feedback.text": "Are you experiencing accessibility issues on this website? Please let us know:",
    "accessibility.feedback.response": "We aim to respond within 5 business days.",
    "accessibility.enforcement.title": "Enforcement",
    "accessibility.enforcement.text":
      "This accessibility statement was prepared on November 18, 2024. If you are not satisfied with our response, you can contact the Netherlands Institute for Human Rights.",

    // Cookies
    "cookies.title": "This website uses cookies",
    "cookies.description":
      "We only use functional cookies that are necessary for the operation of this website. These cookies do not collect personal data and do not require consent under GDPR.",
    "cookies.accept": "Accept",
    "cookies.decline": "Decline",

    // Approach Page
    "approach.title": "Our Approach",
    "approach.intro":
      "Our approach is built on three principles: clarity, collaboration and continuity. We want every client to know exactly where they stand, what’s needed, and how we’ll get there together.",
    "approach.step1.title": "QuickScan (optional, but popular)",
    "approach.step1.description":
      "We start with a quick, accessible scan of your website, immediately highlighting the main areas of concern.",
    "approach.step1.benefit1": "Results within 24–48 hours",
    "approach.step1.benefit2": "Ideal for initial introduction",
    "approach.step1.benefit3": "Provides immediate direction",
    "approach.step2.title": "Full WCAG 2.2 Audit",
    "approach.step2.description":
      "Choosing a full audit? We examine your website page by page against all relevant WCAG 2.2 success criteria.",
    "approach.step2.areasTitle": "We evaluate:",
    "approach.step2.area1": "Technical aspects",
    "approach.step2.area2": "Content",
    "approach.step2.area3": "Design and user experience",
    "approach.step2.area4": "Keyboard navigation",
    "approach.step2.area5": "Color and contrast",
    "approach.step2.area6": "Multimedia",
    "approach.step2.area7": "Forms",
    "approach.step2.area8": "Structure and semantics",
    "approach.step2.footer": "Everything is carefully tested and documented.",
    "approach.step3.title": "Clear Reporting",
    "approach.step3.description": "The result is a clear and practical report that provides insight into:",
    "approach.step3.include1": "What's already working well",
    "approach.step3.include2": "Where improvements are needed",
    "approach.step3.include3": "The priority of each issue",
    "approach.step3.include4": "How to resolve it",
    "approach.step3.footer":
      "A report written in clear, accessible language, so everyone understands what needs to be done.",
    "approach.step4.title": "Personal Walkthrough",
    "approach.step4.description": "We personally review the report with you. Via video call or on-site, we discuss:",
    "approach.step4.topic1": "The findings",
    "approach.step4.topic2": "The impact",
    "approach.step4.topic3": "The order of resolution",
    "approach.step4.topic4": "Your questions",
    "approach.step4.footer": "So everyone knows exactly where they stand.",
    "approach.step5.title": "Implementation Support",
    "approach.step5.description":
      "Does your organization need support implementing the improvements? No problem. We can review, advise, and assist with:",
    "approach.step5.support1": "Development",
    "approach.step5.support2": "Content adjustments",
    "approach.step5.support3": "Design",
    "approach.step5.support4": "Testing",
    "approach.step5.footer": "We work together with your team or your web developer.",
    "approach.step6.title": "Monitoring & Annual Subscriptions",
    "approach.step6.description":
      "Accessibility is constantly evolving. That's why we offer annual subscriptions for businesses that want to maintain structural accessibility.",
    "approach.step6.includes": "Such a subscription includes:",
    "approach.step6.feature1": "Periodic re-audits",
    "approach.step6.feature2": "Updates according to new guidelines",
    "approach.step6.feature3": "Monitoring of key pages",
    "approach.step6.feature4": "Support for questions",
    "approach.step6.feature5": "Annual accessibility statement",
    "approach.step6.footer": "This keeps your website demonstrably compliant and up to date.",
    "approach.step7.title": "Certification (if desired)",
    "approach.step7.description":
      "Is the website fully accessible according to WCAG 2.2? We can prepare an accessibility statement that meets European requirements.",
    "approach.step7.footer": "Perfect for building trust with customers, partners, and government entities.",
    "approach.summary.title": "In Short",
    "approach.summary.description":
      "Our approach is built on three principles: clarity, collaboration, and continuity. We want every client to know exactly where they stand, what's needed, and how we'll achieve it together.",

    // Services Page
    "services.title": "Our Services",
    "services.intro":
      "EqualAccess offers a complete package of services to make and keep your website accessible. From quick scans to fully guided programs.",
    "services.pricing.title": "Also when it comes to pricing.",
    "services.pricing.description": "We work with transparent, fixed rates so you know exactly what to expect.",
    "services.pricing.disclaimer": "All amounts are starting prices and exclude VAT.",
    "services.quickscan.title": "QuickScan",
    "services.quickscan.description":
      "A quick, accessible scan of your website that identifies the main accessibility issues.",
    "services.quickscan.feature1": "Results within 24-48 hours",
    "services.quickscan.feature2": "Overview of main bottlenecks",
    "services.quickscan.feature3": "Ideal for initial introduction",
    "services.quickscan.feature4": "Clear prioritization of issues",
    "services.quickscan.price": "From €395",
    "services.quickscan.cta": "Request now",
    "services.mostPopular": "Most popular",
    "services.audit.title": "Complete WCAG 2.2 Audit",
    "services.audit.description":
      "A complete, professional audit according to all WCAG 2.2 success criteria. Tested and documented page by page.",
    "services.audit.feature1": "Comprehensive report with findings",
    "services.audit.feature2": "Practical improvement advice",
    "services.audit.feature3": "Test on all WCAG 2.2 Level AA criteria",
    "services.audit.feature4": "Including personal walkthrough",
    "services.audit.price": "From €1,995",
    "services.subscription.title": "Annual Subscriptions",
    "services.subscription.description":
      "Structural monitoring and support to keep your website continuously accessible. No worries, just compliance.",
    "services.subscription.feature1": "Periodic re-audits",
    "services.subscription.feature2": "Updates on new guidelines",
    "services.subscription.feature3": "Monitoring of key pages",
    "services.subscription.feature4": "Annual accessibility statement",
    "services.subscription.price": "From €999 per year",
    "services.implementation.title": "Implementation Support",
    "services.implementation.description":
      "Help implementing improvements. We think along, review, and guide your team or web developer.",
    "services.implementation.feature1": "Development guidance",
    "services.implementation.feature2": "Content adjustment support",
    "services.implementation.feature3": "Design change assistance",
    "services.implementation.feature4": "Testing and validation",
    "services.implementation.price": "On quotation",
    "services.certification.title": "Certification & Statements",
    "services.certification.description":
      "Official accessibility statement according to European requirements when your website is fully compliant.",
    "services.certification.feature1": "Complete accessibility statement",
    "services.certification.feature2": "Compliant with European legislation",
    "services.certification.feature3": "Trust with customers and partners",
    "services.certification.feature4": "Suitable for government organizations",
    "services.certification.comingSoon": "This service is not yet available, but we are working towards it.",
    "services.certification.badge": "Coming soon",
    "services.cta.title": "Curious what we can do for you?",
    "services.cta.description": "Contact us without obligation and discover which service best suits your situation.",
    "services.cta.button": "Schedule a call",

    // FAQ
    "faq.title": "Frequently Asked Questions",
    "faq.subtitle": "The most common questions about digital accessibility and WCAG 2.2",
    "faq.q1.question": "What is WCAG 2.2 and why is it important?",
    "faq.q1.answer":
      "WCAG 2.2 (Web Content Accessibility Guidelines) are international guidelines that ensure websites are accessible to everyone, including people with disabilities. From June 28, 2025, Dutch organizations are legally required to comply. It's not just about compliance, but primarily about equal access to digital information for all users.",
    "faq.q2.question": "How long does a complete WCAG audit take?",
    "faq.q2.answer":
      "A complete WCAG 2.2 audit takes an average of 2 to 4 weeks, depending on the size and complexity of your website. A QuickScan, on the other hand, delivers an initial overview of the main points of attention within 24-48 hours. After the audit, you receive a clear report with concrete improvement points and priorities.",
    "faq.q3.question": "What are the costs of an accessibility audit?",
    "faq.q3.answer":
      "Costs vary based on the size and complexity of your website. A QuickScan is an affordable, accessible option to start with. For a complete WCAG 2.2 audit, we're happy to provide a customized quote. We also offer annual subscriptions for structural monitoring and support. Contact us without obligation for a concrete proposal.",
    "faq.q4.question": "Can I implement the improvements myself or do you help with that?",
    "faq.q4.answer":
      "Both options are possible. Our report is so clearly structured that your own team or web developer can get started with it directly. But if you want guidance, we're happy to help with implementation. We can think along, review, and provide concrete support with development, design, content, or testing. You choose which form of collaboration works best for you.",

    // Knowledge Base / Kennisbank
    "nav.knowledge": "Knowledge Base",
    "nav.knowledge.digital": "Digital accessibility",
    "nav.knowledge.disabilities": "Types of disabilities",
    "nav.knowledge.wcag": "The WCAG",
    "nav.knowledge.wdo": "European Accessibility Act",

    // Knowledge CTA
    "knowledge.cta.title": "Need help with accessibility?",
    "knowledge.cta.description": "Contact us and discover how we can help you.",
    "knowledge.cta.button": "Schedule a call",

    // Digital Accessibility Page
    "knowledge.digital.title": "Digital Accessibility",
    "knowledge.digital.intro":
      "Digital accessibility ensures that websites, apps, and digital documents can be used by everyone, regardless of disability or technology used.",
    "knowledge.digital.what.title": "What is digital accessibility?",
    "knowledge.digital.what.text":
      "Digital accessibility means that digital products and services are designed and built to be usable by everyone. This includes people with visual, auditory, motor, or cognitive disabilities, as well as people who are temporarily limited or in certain situations.",
    "knowledge.digital.aspect1.title": "Technology",
    "knowledge.digital.aspect1.desc":
      "A website's code must meet standards so that assistive technology can interpret the content.",
    "knowledge.digital.aspect2.title": "User Experience",
    "knowledge.digital.aspect2.desc": "A website must be intuitive and easy to navigate for all users.",
    "knowledge.digital.aspect3.title": "Legislation",
    "knowledge.digital.aspect3.desc":
      "More and more organizations are legally required to make their digital services accessible.",
    "knowledge.digital.aspect4.title": "Inclusivity",
    "knowledge.digital.aspect4.desc":
      "Accessibility is about equal opportunities and creating an inclusive digital world.",
    "knowledge.digital.why.title": "Why is it important?",
    "knowledge.digital.why.text":
      "Approximately 30% of the Dutch population has some form of disability (source: CBS 2023), globally this is about 16% (source: WHO). Digital accessibility ensures that this large group of people can fully participate in the digital society. Additionally, accessibility improves the user experience for everyone.",
    "knowledge.digital.who.title": "Who is it relevant for?",
    "knowledge.digital.who.text":
      "Digital accessibility is relevant for every organization with an online presence. Government agencies are legally required, but companies in the private sector are also increasingly facing accessibility requirements through the European Accessibility Act.",

    // Disabilities Page
    "knowledge.disabilities.title": "Types of Disabilities",
    "knowledge.disabilities.intro":
      "To understand digital accessibility, it's important to know what types of disabilities exist and how they affect website use.",
    "knowledge.disabilities.visual.title": "Visual Disabilities",
    "knowledge.disabilities.visual.desc":
      "This includes blindness, low vision, color blindness, and light sensitivity. Users rely on screen readers, braille displays, or adjustments in contrast and font size.",
    "knowledge.disabilities.auditory.title": "Auditory Disabilities",
    "knowledge.disabilities.auditory.desc":
      "Deafness and hearing impairment make audio content inaccessible. Subtitles, transcripts, and visual alternatives are essential.",
    "knowledge.disabilities.motor.title": "Motor Disabilities",
    "knowledge.disabilities.motor.desc":
      "Limited hand function, tremors, or paralysis make mouse use difficult. Keyboard navigation and voice control are important alternatives.",
    "knowledge.disabilities.cognitive.title": "Cognitive Disabilities",
    "knowledge.disabilities.cognitive.desc":
      "Dyslexia, ADHD, autism, and memory problems require clear language, simple navigation, and consistent layouts.",
    "knowledge.disabilities.temporary.title": "Temporary Disabilities",
    "knowledge.disabilities.temporary.text":
      "Disabilities are not always permanent. A broken arm, eye surgery, or concussion can temporarily affect how someone uses a website. Accessible websites help these users too.",
    "knowledge.disabilities.situational.title": "Situational Disabilities",
    "knowledge.disabilities.situational.text":
      "Sometimes the environment is the disability: bright sun on a screen, a noisy environment, or holding a baby. Accessibility measures help everyone in these situations.",

    // WCAG Page
    "knowledge.wcag.title": "The WCAG",
    "knowledge.wcag.intro":
      "The Web Content Accessibility Guidelines (WCAG) are the international standard for digital accessibility. Find everything about the WCAG guidelines for digital accessibility here.",
    "knowledge.wcag.what.title": "What is WCAG?",
    "knowledge.wcag.what.text":
      "WCAG stands for Web Content Accessibility Guidelines. They are internationally accepted guidelines that describe how web content can be made accessible to people with disabilities. The guidelines were developed by the W3C (World Wide Web Consortium).",
    "knowledge.wcag.principles.title": "The four principles",
    "knowledge.wcag.perceivable.title": "Perceivable",
    "knowledge.wcag.perceivable.desc":
      "Information and user interface components must be presented in ways users can perceive.",
    "knowledge.wcag.operable.title": "Operable",
    "knowledge.wcag.operable.desc":
      "User interface components and navigation must be operable, including with keyboard only.",
    "knowledge.wcag.understandable.title": "Understandable",
    "knowledge.wcag.understandable.desc":
      "Information and the operation of the user interface must be understandable to users.",
    "knowledge.wcag.robust.title": "Robust",
    "knowledge.wcag.robust.desc":
      "Content must be robust enough to be reliably interpreted by various assistive technologies.",
    "knowledge.wcag.levels.title": "Levels A, AA, and AAA",
    "knowledge.wcag.levels.text":
      "WCAG has three conformance levels. Level A is the minimum, level AA is the standard often legally required, and level AAA is the highest level. Most organizations aim for level AA.",
    "knowledge.wcag.versions.title": "WCAG versions",
    "knowledge.wcag.versions.text":
      "WCAG 2.0 appeared in 2008, followed by 2.1 in 2018 and 2.2 in 2023. Each new version adds criteria but retains the existing ones. WCAG 2.2 Level AA is currently the standard in Dutch and European legislation.",
    "knowledge.wcag.related.title": "Also read",
    "knowledge.wcag.link1": "What is digital accessibility?",
    "knowledge.wcag.link2": "Types of disabilities",
    "knowledge.wcag.link3": "European Accessibility Act (EAA)",
    "knowledge.wcag.link4": "Accessibility tools",
    "knowledge.wcag.link5": "Accessibility checklist",

    // EAA Page (formerly WDO)
    "knowledge.wdo.title": "European Accessibility Act (EAA)",
    "knowledge.wdo.intro":
      "The European Accessibility Act (EAA) has been in effect since June 28, 2025. This European law requires businesses in the private sector to make their digital products and services accessible.",
    "knowledge.wdo.about.title": "What is the EAA?",
    "knowledge.wdo.about.text":
      "The European Accessibility Act is a European directive that sets accessibility requirements for products and services. Unlike previous legislation that only applied to governments, the EAA also affects businesses. The law came into effect on June 28, 2025 and now applies to all EU member states.",
    "knowledge.wdo.who.title": "Who does the EAA apply to?",
    "knowledge.wdo.who.desc":
      "The EAA applies to companies offering products and services in sectors such as e-commerce, banking, transport, telecommunications, and electronic devices. Online shops and digital service providers also fall under this law.",
    "knowledge.wdo.when.title": "Since when?",
    "knowledge.wdo.when.desc":
      "The EAA came into effect on June 28, 2025. Companies must now comply with accessibility requirements. Products and services that were on the market before this date may have a transition period in some cases.",
    "knowledge.wdo.what.title": "What needs to happen?",
    "knowledge.wdo.what.desc":
      "Websites, online shops, and apps must comply with WCAG 2.1 level AA. This means digital services must be perceivable, operable, understandable, and robust for all users, including people with disabilities.",
    "knowledge.wdo.consequences.title": "Consequences",
    "knowledge.wdo.consequences.desc":
      "Companies that do not comply with the EAA may face fines, consumer claims, and reputational damage. Regulators can take enforcement action.",
    "knowledge.wdo.eaa.title": "And the Dutch Digital Government Act?",
    "knowledge.wdo.eaa.text":
      "The Dutch Digital Government Act (WDO) was the predecessor for governments. This Dutch law has required government organizations to be accessible since 2019-2021. The EAA builds on this but extends the obligation to the private sector. The WDO still applies to governments, while the EAA is now leading for businesses.",
    "knowledge.wdo.action.title": "What now?",
    "knowledge.wdo.action.text":
      "The EAA is now in effect. If your website or online shop is not yet accessible, it is important to take action quickly. Start with an audit to know where you stand, and work step by step towards full compliance.",

    // Knowledge Base - Sources
    "knowledge.sources.title": "Sources",
    "knowledge.sources.government": "Government accessibility portal",
    "knowledge.sources.accessibility": "Accessible government websites",
    "knowledge.sources.cbs": "Statistics on people with disabilities",
    "knowledge.sources.specification": "Full specification",
    "knowledge.sources.norm": "What is the standard?",

    // Navigation - Knowledge Base Extra
    "nav.knowledge.tools": "Tools",
    "nav.knowledge.checklist": "Checklist",
    "nav.knowledge.mistakes": "Common mistakes",

    // Tools Page
    "knowledge.tools.title": "Accessibility Tools",
    "knowledge.tools.intro":
      "There are many free and paid tools available to test the accessibility of your website. Discover which tools you can use.",
    "knowledge.tools.why.title": "Why use tools?",
    "knowledge.tools.why.text":
      "Accessibility tools help identify issues that are difficult to see with the naked eye. They automate parts of the testing process and provide insight into technical issues. However, tools only find 30-40% of all accessibility issues - manual testing remains essential.",
    "knowledge.tools.popular.title": "Popular tools",
    "knowledge.tools.wave.title": "WAVE",
    "knowledge.tools.wave.desc":
      "Free browser extension that provides visual feedback on accessibility issues directly on the page.",
    "knowledge.tools.axe.title": "axe DevTools",
    "knowledge.tools.axe.desc":
      "Powerful browser extension for developers with detailed technical reports and suggestions.",
    "knowledge.tools.lighthouse.title": "Lighthouse",
    "knowledge.tools.lighthouse.desc":
      "Built-in Chrome tool that tests accessibility alongside performance and provides a score.",
    "knowledge.tools.validator.title": "W3C Validator",
    "knowledge.tools.validator.desc": "Checks if your HTML is valid, which forms a basis for good accessibility.",
    "knowledge.tools.nvda.title": "NVDA",
    "knowledge.tools.nvda.desc":
      "Free screen reader for Windows that lets you test how blind users experience your site.",
    "knowledge.tools.contrast.title": "Contrast Checker",
    "knowledge.tools.contrast.desc": "Tests whether your color combinations have sufficient contrast for readability.",
    "knowledge.tools.manual.title": "Manual testing",
    "knowledge.tools.manual.text":
      "Tools are valuable but not sufficient. Always test manually too: navigate with just the keyboard, test with a screen reader, and ask users with disabilities for feedback.",
    "knowledge.tools.professional.title": "Professional audit",
    "knowledge.tools.professional.text":
      "For a complete picture of your website's accessibility, a professional audit is needed. Experts combine automated tests with manual checks and also assess user experience and compliance with legislation.",
    "knowledge.tools.related.title": "Also read",
    "knowledge.tools.link1": "Accessibility checklist",
    "knowledge.tools.link2": "Common mistakes",
    "knowledge.tools.link3": "The WCAG",

    // Checklist Page
    "knowledge.checklist.title": "Accessibility Checklist",
    "knowledge.checklist.intro":
      "Use this practical checklist to check the basic accessibility of your website. This is a starting point - a professional audit is needed for full compliance.",
    "knowledge.checklist.how.title": "How to use this checklist",
    "knowledge.checklist.how.text":
      "Go through each part of your website using these checkpoints. Mark what's already good and note what needs improvement. This checklist covers the key WCAG 2.2 Level A and AA criteria.",
    "knowledge.checklist.images.title": "Images",
    "knowledge.checklist.images.item1": "All informative images have descriptive alt text",
    "knowledge.checklist.images.item2": 'Decorative images have an empty alt attribute (alt="")',
    "knowledge.checklist.images.item3": "Complex images have an extended description available",
    "knowledge.checklist.text.title": "Text & Typography",
    "knowledge.checklist.text.item1": "Text can be enlarged to 200% without loss of functionality",
    "knowledge.checklist.text.item2": "Font is readable and at least 16px for body text",
    "knowledge.checklist.text.item3": "Text alignment is left (not justified)",
    "knowledge.checklist.navigation.title": "Navigation",
    "knowledge.checklist.navigation.item1": "All functions are accessible with keyboard only",
    "knowledge.checklist.navigation.item2": "There is a skip link to the main content",
    "knowledge.checklist.navigation.item3": "The navigation order is logical and predictable",
    "knowledge.checklist.color.title": "Colors & Contrast",
    "knowledge.checklist.color.item1": "Text contrast is at least 4.5:1 (small text) or 3:1 (large text)",
    "knowledge.checklist.color.item2": "Information is not conveyed by color alone",
    "knowledge.checklist.color.item3": "Focus indicators are clearly visible",
    "knowledge.checklist.media.title": "Multimedia",
    "knowledge.checklist.media.item1": "Videos have subtitles",
    "knowledge.checklist.media.item2": "Audio content has a transcript",
    "knowledge.checklist.media.item3": "No auto-playing media with sound",
    "knowledge.checklist.forms.title": "Forms",
    "knowledge.checklist.forms.item1": "All form fields have visible labels",
    "knowledge.checklist.forms.item2": "Error messages are clear and descriptive",
    "knowledge.checklist.forms.item3": "Required fields are clearly indicated",
    "knowledge.checklist.note.title": "Important",
    "knowledge.checklist.note.text":
      "This checklist is a starting point and does not cover all WCAG criteria. For full compliance and an accessibility statement, a professional audit is necessary. Contact us for a thorough analysis.",
    "knowledge.checklist.related.title": "Also read",
    "knowledge.checklist.link1": "Accessibility tools",
    "knowledge.checklist.link2": "Common mistakes",
    "knowledge.checklist.link3": "The WCAG",

    // Common Mistakes Page
    "knowledge.mistakes.title": "Common Mistakes",
    "knowledge.mistakes.intro":
      "Discover the most common accessibility mistakes on websites and learn how to avoid or fix them.",
    "knowledge.mistakes.why.title": "Why mistakes occur",
    "knowledge.mistakes.why.text":
      "Accessibility mistakes often arise from lack of knowledge, time pressure, or not including accessibility in the development process. By knowing the most common mistakes, you can proactively avoid them.",
    "knowledge.mistakes.alt.title": "Missing or poor alt texts",
    "knowledge.mistakes.alt.desc":
      'Images without alt text or with meaningless descriptions like "image" or "photo" are unusable for screen readers.',
    "knowledge.mistakes.alt.solution":
      'Solution: Describe what the image shows or communicates. Decorative images get alt="".',
    "knowledge.mistakes.contrast.title": "Insufficient color contrast",
    "knowledge.mistakes.contrast.desc":
      "Light gray text on white or subtle color combinations make text hard to read for many people.",
    "knowledge.mistakes.contrast.solution": "Solution: Use a contrast checker to ensure at least 4.5:1 contrast ratio.",
    "knowledge.mistakes.keyboard.title": "No keyboard accessibility",
    "knowledge.mistakes.keyboard.desc":
      "Interactive elements that only work with a mouse exclude users who cannot use a mouse.",
    "knowledge.mistakes.keyboard.solution": "Solution: Test all functionality with just Tab, Enter, and arrow keys.",
    "knowledge.mistakes.labels.title": "Forms without labels",
    "knowledge.mistakes.labels.desc": "Input fields without linked labels are incomprehensible for screen readers.",
    "knowledge.mistakes.labels.solution": "Solution: Use <label> elements linked to input fields via for/id.",
    "knowledge.mistakes.headings.title": "Incorrect heading structure",
    "knowledge.mistakes.headings.desc": "Headings used only for styling or that skip levels disrupt the page order.",
    "knowledge.mistakes.headings.solution": "Solution: Use headings (h1-h6) in logical order to structure the page.",
    "knowledge.mistakes.focus.title": "Invisible focus indicator",
    "knowledge.mistakes.focus.desc": "Removing the default focus outline makes keyboard navigation impossible.",
    "knowledge.mistakes.focus.solution": "Solution: Keep or improve the focus indicator, never remove it completely.",
    "knowledge.mistakes.links.title": "Unclear link texts",
    "knowledge.mistakes.links.desc":
      'Links with text like "click here" or "read more" provide no context outside the sentence.',
    "knowledge.mistakes.links.solution": "Solution: Use descriptive link text that indicates the destination.",
    "knowledge.mistakes.language.title": "No language attribute",
    "knowledge.mistakes.language.desc":
      "Without a lang attribute, screen readers don't know which pronunciation to use.",
    "knowledge.mistakes.language.solution": 'Solution: Add lang="en" to the <html> element.',
    "knowledge.mistakes.prevention.title": "Preventing mistakes",
    "knowledge.mistakes.prevention.text":
      "The best way to prevent accessibility mistakes is to include accessibility from the start in the design and development process. Train your team, test regularly, and ask users with disabilities for feedback.",
    "knowledge.mistakes.related.title": "Also read",
    "knowledge.mistakes.link1": "Accessibility checklist",
    "knowledge.mistakes.link2": "Accessibility tools",
    "knowledge.mistakes.link3": "The WCAG",

    // QuickScan Request Page
    "quickscan.request.pageTitle": "Request QuickScan",
    "quickscan.request.step1.label": "Info",
    "quickscan.request.step2.label": "Personal",
    "quickscan.request.step3.label": "Company",
    "quickscan.request.step4.label": "Confirm",
    "quickscan.request.step1.title": "The QuickScan",
    "quickscan.request.step1.subtitle":
      "A quick, accessible scan of your website that identifies the main accessibility issues.",
    "quickscan.request.step1.whatYouGet": "What do you get?",
    "quickscan.request.step1.feature1": "An overview of the biggest accessibility bottlenecks on your website",
    "quickscan.request.step1.feature2": "Clear prioritization of areas of concern",
    "quickscan.request.step1.feature3": "Practical recommendations to get started immediately",
    "quickscan.request.step1.feature4": "Insight into your current WCAG 2.2 compliance level",
    "quickscan.request.step1.feature5": "An excellent starting point for further accessibility improvements",
    "quickscan.request.step1.delivery": "Within 24-48 hours",
    "quickscan.request.step1.report": "Clear report",
    "quickscan.request.step1.actionable": "Immediately actionable",
    "quickscan.request.step1.priceNote": "Excluding VAT",
    "quickscan.request.step2.title": "Your details",
    "quickscan.request.step2.subtitle": "Fill in your personal details so we can contact you.",
    "quickscan.request.step3.title": "Company details",
    "quickscan.request.step3.subtitle": "Fill in your organization's details.",
    "quickscan.request.step4.title": "Review your request",
    "quickscan.request.step4.subtitle": "Check the entered information and confirm your request.",
    "quickscan.request.step4.personalInfo": "Personal information",
    "quickscan.request.step4.companyInfo": "Company information",
    "quickscan.request.step4.declarations": "Declarations",
    "quickscan.request.step4.agreement":
      "I agree to the cost of €395 excluding VAT for the QuickScan. After confirmation, I will receive an invoice and the scan will be performed within 24-48 hours after payment.",
    "quickscan.request.step4.termsAgreement": "I agree to the",
    "quickscan.request.step4.termsLink": "terms and conditions",
    "quickscan.request.step4.and": "and",
    "quickscan.request.step4.privacyLink": "privacy policy",
    "quickscan.request.step4.marketingOptIn":
      "I would like to receive news, tips and updates about digital accessibility by email.",
    "quickscan.request.validation.terms": "You must agree to the terms and conditions to proceed.",
    "quickscan.request.form.name": "Name",
    "quickscan.request.form.function": "Position",
    "quickscan.request.form.phone": "Phone number",
    "quickscan.request.form.email": "Email address",
    "quickscan.request.form.website": "Website URL",
    "quickscan.request.form.companyName": "Company name",
    "quickscan.request.form.companyNameHint": "As registered at the Chamber of Commerce",
    "quickscan.request.form.street": "Street + house number",
    "quickscan.request.form.postalCode": "Postal code",
    "quickscan.request.form.city": "City",
    "quickscan.request.form.country": "Country",
    "quickscan.request.form.vatNumber": "VAT number",
    "quickscan.request.form.address": "Address",
    "quickscan.request.back": "Previous",
    "quickscan.request.next": "Next",
    "quickscan.request.submit": "Submit request",
    "quickscan.request.validation.error": "Check the details",
    "quickscan.request.validation.personal": "Please fill in at least your name and email address.",
    "quickscan.request.validation.company":
      "Please fill in at least your website, company name, street, postal code and city.",
    "quickscan.request.validation.agreement": "You must agree to the costs to submit the request.",
    "quickscan.request.validation.nameRequired": "Name is required.",
    "quickscan.request.validation.emailRequired": "Email address is required.",
    "quickscan.request.validation.emailInvalid": "Please enter a valid email address.",
    "quickscan.request.validation.websiteRequired": "Website is required.",
    "quickscan.request.validation.companyNameRequired": "Company name is required.",
    "quickscan.request.validation.streetRequired": "Street and house number is required.",
    "quickscan.request.validation.postalCodeRequired": "Postal code is required.",
    "quickscan.request.validation.cityRequired": "City is required.",
    "quickscan.request.success.title": "Request received!",
    "quickscan.request.success.description": "Thank you for your request. We will contact you as soon as possible.",
    "quickscan.request.success.screen.title": "Your request has been received!",
    "quickscan.request.success.screen.subtitle":
      "Thank you for your interest in our QuickScan. We will start working on it right away.",
    "quickscan.request.success.screen.emailTitle": "Email confirmation",
    "quickscan.request.success.screen.emailDescription":
      "You will receive a confirmation email within a few minutes at the provided email address.",
    "quickscan.request.success.screen.nextSteps": "What happens now?",
    "quickscan.request.success.screen.step1": "We will review your request and website",
    "quickscan.request.success.screen.step2": "Within 5 business days you will receive your QuickScan report",
    "quickscan.request.success.screen.step3": "You will receive an invoice for €395 excl. VAT",
    "quickscan.request.success.screen.newRequest": "New request",
    "quickscan.request.success.screen.backHome": "Back to home",
    "quickscan.request.error.title": "Something went wrong",
    "quickscan.request.error.description": "Your request could not be submitted. Please try again later.",
    "quickscan.request.submitting": "Submitting...",

    // Booking Request Page
    "booking.request.pageTitle": "Schedule a call",
    "booking.request.step1.label": "Date & Time",
    "booking.request.step2.label": "Details",
    "booking.request.step3.label": "Topic",
    "booking.request.step4.label": "Summary",
    "booking.request.step1.title": "Choose a date and time slot",
    "booking.request.step1.subtitle": "Select your preferred moment for a call.",
    "booking.request.step1.selectDate": "Select a date",
    "booking.request.step1.selectTime": "Select a time slot",
    "booking.request.step1.selected": "Your selection",
    "booking.request.step1.at": "at",
    "booking.request.step2.title": "Your details",
    "booking.request.step2.subtitle": "Fill in your contact details so we can reach you.",
    "booking.request.step3.title": "Topic of the conversation",
    "booking.request.step3.subtitle": "Briefly describe what you would like to discuss.",
    "booking.request.step4.title": "Review your details",
    "booking.request.step4.subtitle": "Check the entered information and confirm your request.",
    "booking.request.step4.dateTime": "Date and time",
    "booking.request.step4.personalInfo": "Your details",
    "booking.request.step4.topic": "Topic",
    "booking.request.form.name": "Name",
    "booking.request.form.companyName": "Company name",
    "booking.request.form.jobTitle": "Job title",
    "booking.request.form.phone": "Phone number",
    "booking.request.form.email": "Email address",
    "booking.request.form.emailHint": "Optional - for email confirmation",
    "booking.request.form.topic": "Question or topic",
    "booking.request.form.topicPlaceholder": "How can we help you? Briefly describe your question or topic...",
    "booking.request.back": "Previous",
    "booking.request.next": "Next",
    "booking.request.submit": "Confirm appointment",
    "booking.request.submitting": "Submitting...",
    "booking.request.validation.error": "Check the details",
    "booking.request.validation.dateTime": "Please select a date and time slot.",
    "booking.request.validation.personal": "Please fill in at least your name, company name and phone number.",
    "booking.request.validation.topic": "Please briefly describe the topic of the conversation.",
    "booking.request.success.title": "Your appointment has been requested!",
    "booking.request.success.subtitle": "Thank you for your request. We will contact you as soon as possible.",
    "booking.request.success.emailTitle": "Email confirmation",
    "booking.request.success.emailDescription":
      "You will receive a confirmation email within a few minutes at the provided email address.",
    "booking.request.success.nextSteps": "What happens now?",
    "booking.request.success.step1": "We will review your request and contact you",
    "booking.request.success.step2": "We will confirm the appointment by phone or email",
    "booking.request.success.step3": "We will call you at the agreed date and time",
    "booking.request.success.newRequest": "Schedule another appointment",
    "booking.request.success.backHome": "Back to home",
    "booking.request.error.title": "Something went wrong",
    "booking.request.error.description": "Your request could not be submitted. Please try again later.",
  },
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<Language>("nl");

  // Update HTML lang attribute when language changes (WCAG 3.1.1)
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const t = (key: string): string => {
    return translations[language][key as keyof (typeof translations)["nl"]] || key;
  };

  return <LanguageContext.Provider value={{ language, setLanguage, t }}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
